/**
 * PROG-DETAIL-NEW/1 (#259a03b2) — read-only port of the library detail
 * chrome into the Programmazione Detail page.
 *
 * Mirrors `pages/PrecompiledProgressions/PrecompiledProgressionDetail.tsx`:
 * - collapsible ppd-week-card chrome (first week open, rest closed)
 * - per-set SetDetailCard with the same field rows (Carico rif.,
 *   Carico ipotetico template, Ipotetico da sorgente, Reps ipotetiche,
 *   Work Reps, Work Load, Carico/Reps ipotetici futuri, StS/SiS kind chip,
 *   applyForNext, mini-sets, Catena ipotetici)
 *
 * v1 is read-only — no pencil affordances, no mutations. DETAIL-NEW-3
 * adds the edit affordances; DETAIL-NEW-2 adds the BE mutations.
 *
 * Reuses the shared engine helpers from `utils/precompiledRuleApply` and
 * `utils/precompiledChain` (and `utils/progressionEngine` for mini-set +
 * workFuturo computations) so the projected numbers match the library
 * Detail page by construction. The `computeHypLoadGrid` (with ENGINE-2
 * walk-back) lives in `ExerciseProgressionEditor`; re-exporting it from
 * there keeps a single source for the editor + detail.
 */
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  Dropdown,
  Grid,
  Image,
  Input,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import type { MenuProps } from "antd";
import DecimalInputNumber from "../../common/DecimalInputNumber";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faCopy,
  faDumbbell,
  faEllipsis,
  faPaste,
  faPlus,
  faSliders,
} from "@fortawesome/free-solid-svg-icons";
import type {
  MiniSet,
  PrecompiledSet,
  PrecompiledWeek,
  PrecompiledWeekProgressionRule,
  ProgressionMethodConfig,
  SetItem,
  SogliaItem,
  SogliaThreshold,
} from "../../types/Entities";
import type { ExerciseProgression } from "../../types/Programmazione";
import {
  computePrevSlotProjection,
  normalizePastedRule,
  resolveSourceSlot,
} from "../../utils/precompiledRuleApply";
import { useClipboard } from "../../contexts/clipboard";
import { useToasts } from "../../contexts/toasts";
import { useUploadIndicator } from "../../contexts/uploadIndicator";
import {
  computeMiniSetLoads,
  computeNextSetInSetLoad,
  computeNextSetInSetReps,
  computeNextWeekLoad,
  computeNextWeekReps,
} from "../../utils/progressionEngine";
import { simulateChain } from "../../utils/precompiledChain";
import { computeHypLoadGrid } from "../Form/ExerciseProgressionEditor";
import { computeHypRepsGrid } from "../../utils/precompiledRuleApply";
import type { ProgEditorOptions } from "../../hooks/useEntityOptions";
import { useProgrammazioneMutations } from "../../hooks/useProgrammazioneMutations";
import InlineEditModal from "../InlineEditModal";
import ProgressionRuleEditor from "../Form/ProgressionRuleEditor";
import { AllegatiPanel } from "../Form/ExerciseProgressionSetRow";
import WeekDateControl from "../Form/WeekDateControl";
import "../../pages/PrecompiledProgressions/PrecompiledProgressions.css";

// ─── PROG-DETAIL-NEW/3 — pencil affordance + role gating ──────────────────────
//
// Per-set fields the ATHLETE may edit from the Detail page (Q1 user decision).
// DETAIL-NEW/6 added the `media` pencil (image/video) per the original
// fanout's Q2: athlete may attach media to their own programmazione.
const ATHLETE_FIELDS = new Set<EditableField>([
  "workReps",
  "workLoad",
  "workXReps",
  "workXLoad",
  "note",
  "media",
  // Athlete opens the mini-set editor to fill executed work reps/load; the
  // structure rows (reps/delta) stay trainer-only inside the modal.
  "miniSets",
]);

type EditableField =
  // per-set, both roles
  | "workReps"
  | "workLoad"
  | "workXReps"
  | "workXLoad"
  | "note"
  // per-set, both roles — DETAIL-NEW/6 image/video upload modal
  | "media"
  // per-set, trainer-only
  | "repSchemeTypeId"
  | "repScheme"
  | "effortTypeId"
  | "effort"
  | "tut"
  | "tecnica"
  // T2.B (#eda532b5) — Recupero chip (reads `set.rec`, trainer-edit via recOptions).
  | "rec"
  | "label"
  | "trialLoad"
  // per-set, trainer-only — large-modal special case (DETAIL-NEW/4):
  // opens the full ProgressionRuleEditor in a width=900 Modal.
  | "progressionRule"
  // per-set, trainer-only — list-editor modal (DETAIL-NEW/5).
  | "miniSets"
  // per-week, trainer-only
  | "week:repScheme"
  | "week:effort"
  | "week:tut"
  | "week:tecnica"
  | "week:xRepsTarget"
  | "week:xRepsLoadDropPerc"
  | "week:repSchemeTypeId"
  | "week:effortTypeId"
  | "week:rec";

type ModalConfig = {
  fieldLabel: string;
  fieldType: "text" | "number" | "textarea" | "select" | "hybrid";
  value: any;
  options?: { label: string; value: string | number }[];
  optionsLoading?: boolean;
  onSave: (newValue: any) => void;
};

// Pencil button — Q3 user decision: AntD text-button + EditOutlined icon.
function EditPencil({ onClick, title }: { onClick: () => void; title?: string }) {
  return (
    <Button
      type="text"
      size="small"
      icon={<EditOutlined />}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={title}
      aria-label={title}
      style={{ marginLeft: 4, opacity: 0.7 }}
    />
  );
}

// Labeled "Nome: valore" chip. The value truncates on one line; on mobile the
// chip becomes tappable and opens a Popover with the full label + an edit
// button (the inline pencil is desktop-only, where there's room).
function InfoChip({
  name,
  value,
  empty,
  editable,
  onEdit,
  isMobile,
  stopToggle,
  extraAction,
  tone,
}: {
  name: string;
  value: ReactNode;
  empty?: boolean;
  editable: boolean;
  onEdit: () => void;
  isMobile: boolean;
  stopToggle?: boolean;
  // Optional inline action rendered next to the value (e.g. a media preview
  // eye). Always shown (desktop + mobile); it must stopPropagation itself so it
  // doesn't trigger the chip's card-toggle / mobile popover.
  extraAction?: ReactNode;
  // Optional colored-badge variant (work load / work reps / rep scheme /
  // carico suggerito) so these fields read as value badges like the hyp ones.
  tone?: "load" | "reps" | "scheme" | "ref";
}) {
  const [open, setOpen] = useState(false);
  const chip = (
    <span
      className={`ppd-week-chip ppd-week-chip--labeled${
        tone && !empty ? ` ppd-chip--${tone}` : ""
      }`}
      style={isMobile ? { cursor: "pointer" } : undefined}
      onClick={stopToggle ? (e) => e.stopPropagation() : undefined}
    >
      <span className="ppd-week-chip-name">{name}</span>
      <span
        className="ppd-week-chip-value"
        style={empty ? { opacity: 0.5 } : undefined}
      >
        {value}
      </span>
      {extraAction}
      {editable && !isMobile && (
        <EditPencil onClick={onEdit} title={`Modifica ${name}`} />
      )}
    </span>
  );

  if (!isMobile) return chip;

  return (
    <Popover
      trigger="click"
      placement="top"
      open={open}
      onOpenChange={setOpen}
      content={
        <div style={{ maxWidth: 260 }} onClick={(e) => e.stopPropagation()}>
          <div style={{ marginBottom: editable ? 10 : 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--app-gray-500)",
              }}
            >
              {name}
            </div>
            <div style={{ fontWeight: 600 }}>{value}</div>
          </div>
          {editable && (
            <Button
              size="small"
              type="primary"
              icon={<EditOutlined />}
              block
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
            >
              Modifica
            </Button>
          )}
        </div>
      }
    >
      {chip}
    </Popover>
  );
}

// ─── label helpers (mirror PrecompiledProgressionDetail.tsx) ──────────────────

function ruleLabel(
  rule: PrecompiledWeekProgressionRule,
  isFirstWeek: boolean,
  isFirstSet: boolean,
): string | null {
  if (isFirstWeek && isFirstSet) {
    if (!rule.type) return null;
    if (rule.type === "fixed") {
      return rule.initialLoadType === "dynamic"
        ? `W${rule.initialWnRm ?? "?"}Rm × ${rule.initialPnRm ?? "?"}%`
        : rule.initialLoad != null
        ? `${rule.initialLoad} kg (fisso)`
        : "Fisso";
    }
    if (rule.type === "thresholds") {
      const pt = rule.progressionType;
      if (!pt || pt === "none") return "Soglie (nessuna prog.)";
      return pt === "set-to-set"
        ? "Soglie StS"
        : pt === "set-in-set"
        ? "Soglie SiS"
        : "Soglie StS+SiS";
    }
    return null;
  }
  const srcLabel =
    rule.loadComputeSource === "sts"
      ? "StS"
      : rule.loadComputeSource === "sis"
      ? "SiS"
      : rule.loadComputeSource === "both"
      ? "StS+SiS"
      : rule.loadComputeSource === "fixed"
      ? "Fisso"
      : rule.loadComputeSource === "dynamic"
      ? "Dinamico"
      : null;
  const pt = rule.progressionType;
  const ptLabel =
    pt === "set-to-set"
      ? "StS"
      : pt === "set-in-set"
      ? "SiS"
      : pt === "both"
      ? "StS+SiS"
      : null;
  if (srcLabel && ptLabel) return `${srcLabel} → prog. ${ptLabel}`;
  return srcLabel ?? ptLabel ?? null;
}

const stripSetCountPrefix = (s: string | null | undefined): string | null => {
  if (!s) return null;
  const trimmed = s.trim().replace(/^\d+[xX]\s*/i, "");
  return trimmed || null;
};

// T2.B (#eda532b5) — Recupero value formatter per UX REV2 (#d64ed933 d43284c5)
// §5: `${n}s` if <60s, `${Math.floor(n/60)} min` if ≥60s. Pass-through for
// non-numeric strings.
const formatRec = (
  v: string | null | undefined,
  recOptions?: { label: string; value: string | number }[],
): string | null => {
  if (v == null || v === "") return null;
  // If `v` matches a catalog id, prefer the catalog label.
  if (recOptions) {
    const hit = recOptions.find((o) => String(o.value) === String(v));
    if (hit) return hit.label;
  }
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!isNaN(n)) {
    return n < 60 ? `${n}s` : `${Math.floor(n / 60)} min`;
  }
  return String(v);
};

// ─── SetDetailCard ────────────────────────────────────────────────────────────

interface SetDetailCardProps {
  set: PrecompiledSet;
  setIndex: number;
  isFirstWeek: boolean;
  weekRepScheme?: string | null;
  /** PROG-DETAIL-NEW/3 — tecnica.includeXReps gating signal for workXReps /
   *  workXLoad pencils + display. Resolved from `tecnicheRaw` at the week
   *  level so the per-set rendering doesn't re-do the lookup. */
  includeXReps: boolean;
  /** PROG-DETAIL-NEW/5 — tecnica.dropXReps gating signal for the miniSets
   *  pencil. When false, no pencil and no empty Mini-set row is rendered
   *  (matches the editor's gating contract). */
  dropXReps: boolean;
  hypLoad: number | null;
  hypReps: number | string | null;
  prevWeekSet: PrecompiledSet | null;
  prevSetInWeek: PrecompiledSet | null;
  prevWeekHypLoad: number | null;
  prevSetHypLoad: number | null;
  /** PROG-DETAIL-NEW/3 — pencil click handler. `null` when the viewer cannot
   *  edit anything (other-than-trainer + other-than-athlete). */
  onEditSetField: ((field: EditableField, set: PrecompiledSet) => void) | null;
  /** Whole-rule Copia/Incolla for this set. Undefined when not editable. */
  onCopyRule?: () => void;
  onPasteRule?: () => void;
  canPasteRule?: boolean;
  /** Whole-set Copia/Incolla for this set. Undefined when not editable. */
  onCopySet?: () => void;
  onPasteSet?: () => void;
  canPasteSet?: boolean;
  canEditField: (field: EditableField) => boolean;
  /** PROG-ENGINE-V3/5 — full week matrix + current coordinates for the
   *  set-card "Sorgente carico" discoverability chip. */
  weeks: PrecompiledWeek[];
  wi: number;
  /** Catalog options for resolving repSchemeTypeId / effortTypeId names. */
  options?: ProgEditorOptions;
  /** T2.B (#eda532b5) — week-level `rec` value passed through so the Recupero
   *  chip can fall back to it when `set.rec` is null (BE normalizer already
   *  does this on read, so this is defensive only). */
  weekRec?: string | null;
  /** T2.B (#eda532b5) — `detail` reduces the chip whitelist and gates
   *  trainer-only meta (rule chip, rule-meta rows, workFuturo) behind
   *  `isTrainer`. `edit` (default) keeps the full editor surface. */
  displayMode: "detail" | "edit";
  /** T2.B — passed through to the rule chip / rule-meta `isTrainer` gate when
   *  `displayMode === 'detail'` (REV2 §3). */
  isTrainer: boolean;
}

function SetDetailCard({
  set,
  setIndex,
  isFirstWeek,
  weekRepScheme,
  includeXReps,
  dropXReps,
  hypLoad,
  hypReps: hypRepsProjected,
  prevWeekSet,
  prevSetInWeek,
  prevWeekHypLoad,
  prevSetHypLoad,
  onEditSetField,
  onCopyRule,
  onPasteRule,
  canPasteRule,
  onCopySet,
  onPasteSet,
  canPasteSet,
  canEditField,
  weeks,
  wi,
  options,
  weekRec,
  displayMode,
  isTrainer,
}: SetDetailCardProps) {
  // PROG-DETAIL/xReps-per-set — a set can override the week's tecnica, so the
  // mini-set editor must gate on the SET's OWN tecnica when present, not the
  // week-level `includeXReps` prop resolved from `week.tecnica` in the parent.
  // Without this, choosing an X-Reps tecnica on a single set (while the week
  // stays on a non-X-Reps one) never surfaced the mini-set panel. Falls back
  // to the week-level prop when the set inherits (set.tecnica == null).
  const effIncludeXReps = useMemo(() => {
    const t = set.tecnica;
    if (t == null || t === "") return includeXReps;
    const raw = options?.tecnicheRaw ?? [];
    const s = String(t);
    const match = raw.find(
      (x) =>
        String((x as { id?: number }).id) === s ||
        (x as { name?: string }).name === s,
    ) as { includeXReps?: boolean } | undefined;
    return match ? !!match.includeXReps : includeXReps;
  }, [set.tecnica, options, includeXReps]);

  // Show-more toggle for the dense per-set info chips.
  const [setChipsOpen, setSetChipsOpen] = useState(false);
  // Read-only media preview (image/video) opened from the Media chip's eye,
  // separate from the edit modal.
  const [mediaPreview, setMediaPreview] = useState<{
    kind: "image" | "video";
    src: string;
  } | null>(null);
  // Bumped on every open so the <video> is keyed to a fresh element each time
  // (a reused media element only replays on alternate opens).
  const [mediaPreviewNonce, setMediaPreviewNonce] = useState(0);
  const openMediaPreview = useCallback(
    (p: { kind: "image" | "video"; src: string }) => {
      setMediaPreviewNonce((n) => n + 1);
      setMediaPreview(p);
    },
    [],
  );
  const setScreens = Grid.useBreakpoint();
  const isMobile = !setScreens.md;
  // PROG-DETAIL-UX/1 — render an editable per-set field as a chip (same
  // visual pattern as the per-week chips). On mobile the chip is tappable and
  // opens a Popover with the full label + edit button (see InfoChip).
  const renderChip = (
    field: EditableField,
    label: string,
    value: ReactNode,
    isEmpty: boolean,
    tone?: "load" | "reps" | "scheme" | "ref",
  ) => {
    const editable = !!onEditSetField && canEditField(field);
    if (isEmpty && !editable) return null;
    return (
      <InfoChip
        key={field}
        name={label}
        value={value}
        empty={isEmpty}
        editable={editable}
        onEdit={() => onEditSetField!(field, set)}
        isMobile={isMobile}
        tone={tone}
      />
    );
  };
  const rule = set.progressionRule;
  // ENGINE-3 parity: rule-driven hyp reps first, fall back to the week-level
  // repScheme stripped of its `^\d+x` set-count prefix.
  const hypReps =
    hypRepsProjected != null ? hypRepsProjected : stripSetCountPrefix(weekRepScheme);
  const rl = ruleLabel(rule, isFirstWeek, setIndex === 0);
  const hasHyp = hypLoad != null || hypReps != null;
  const hasWorkData = set.workReps != null || set.workLoad != null;

  // source preview (mirrors library Detail's pp-source-preview block).
  const src = rule.loadComputeSource ?? null;
  const isFirstSet = setIndex === 0;
  const showSourcePreview =
    !(isFirstWeek && isFirstSet) &&
    (src === "sts" || src === "sis" || src === "both");
  const stsPreview = showSourcePreview
    ? computePrevSlotProjection({
        target: "sts",
        prevWeekSet,
        prevWeekHypLoad,
        prevWeekSetRule: prevWeekSet?.progressionRule ?? null,
        weekRepScheme,
      })
    : null;
  const sisPreview = showSourcePreview
    ? computePrevSlotProjection({
        target: "sis",
        prevSetInWeek,
        prevSetHypLoad,
        prevSetRule: prevSetInWeek?.progressionRule ?? null,
        weekRepScheme,
      })
    : null;
  const stsLoad = stsPreview?.load ?? null;
  const sisLoad = sisPreview?.load ?? null;
  const stsReps = stsPreview?.reps ?? null;
  const sisReps = sisPreview?.reps ?? null;
  const sourcePreviewLoad: number | null = !showSourcePreview
    ? null
    : src === "sts"
    ? stsLoad
    : src === "sis"
    ? sisLoad
    : rule.calculationPriority === "sis"
    ? sisLoad ?? stsLoad
    : stsLoad ?? sisLoad;
  const sourcePreviewReps: number | string | null = !showSourcePreview
    ? null
    : src === "sts"
    ? stsReps
    : src === "sis"
    ? sisReps
    : rule.calculationPriority === "sis"
    ? sisReps ?? stsReps
    : stsReps ?? sisReps;

  // workReps-driven "futuro" projection (mirrors library Detail Wave 1.3 / FE-2).
  const workFuturo = (() => {
    const wr = set.workReps != null ? parseFloat(String(set.workReps)) : NaN;
    const wl = set.workLoad != null ? Number(set.workLoad) : NaN;
    if (isNaN(wr) || isNaN(wl) || wr <= 0 || wl <= 0) return undefined;
    const pt = rule.progressionType ?? null;
    if (pt === "none" || pt == null) return undefined;
    const ruleKindStS = rule.type;
    const ruleKindSiS = rule.typeSiS ?? rule.type;
    const isStS =
      (pt === "set-to-set" || pt === "both") && ruleKindStS === "thresholds";
    const isSiS =
      (pt === "set-in-set" || pt === "both") && ruleKindSiS === "thresholds";
    if (!isStS && !isSiS) return undefined;
    const ruleTypeStS: "%" | "weight" = rule.loadChangeType === "kg" ? "weight" : "%";
    const ruleTypeSiS: "%" | "weight" =
      rule.loadChangeTypeSiS === "kg" ? "weight" : "%";
    const stsTarget = rule.incrementTarget ?? "load";
    const sisTarget = rule.incrementTargetSiS ?? "load";
    const toLoadSoglie = (
      ts: SogliaThreshold[] | null | undefined,
      t: "%" | "weight",
    ): SogliaItem[] =>
      (ts ?? []).map((th) => ({
        threshold: th.threshold,
        type: t,
        value: th.loadValue ?? 0,
      }));
    const toRepsSoglie = (
      ts: SogliaThreshold[] | null | undefined,
    ): SogliaItem[] =>
      (ts ?? [])
        .filter(
          (th) =>
            th.repsValue !== null &&
            th.repsValue !== undefined &&
            String(th.repsValue) !== "",
        )
        .map((th) => ({
          threshold: th.threshold,
          type: "repScheme" as const,
          value:
            typeof th.repsValue === "number"
              ? th.repsValue
              : String(th.repsValue ?? ""),
        }));
    const mkLoadSynth = (target: "stsLoad" | "sisLoad"): SetItem =>
      target === "stsLoad"
        ? {
            intensityType: "%",
            progressionType: "set-to-set",
            calculationPriority: "soglie",
            workLoad: String(wl),
            repScheme: weekRepScheme ?? undefined,
            incrementRuleStS: {
              type: ruleTypeStS,
              value: 0,
              soglie: toLoadSoglie(rule.positiveThresholdsStS, ruleTypeStS),
            },
            decrementRuleStS: {
              type: ruleTypeStS,
              value: 0,
              soglie: toLoadSoglie(rule.negativeThresholdsStS, ruleTypeStS),
            },
          }
        : {
            intensityType: "%",
            progressionType: "set-in-set",
            calculationPriority: "soglie",
            workLoad: String(wl),
            repScheme: weekRepScheme ?? undefined,
            incrementRuleSiS: {
              type: ruleTypeSiS,
              value: 0,
              soglie: toLoadSoglie(rule.positiveThresholdsSiS, ruleTypeSiS),
            },
            decrementRuleSiS: {
              type: ruleTypeSiS,
              value: 0,
              soglie: toLoadSoglie(rule.negativeThresholdsSiS, ruleTypeSiS),
            },
          };
    const mkRepsSynth = (target: "stsReps" | "sisReps"): SetItem =>
      target === "stsReps"
        ? {
            intensityType: "%",
            progressionType: "set-to-set",
            calculationPriority: "soglie",
            workLoad: String(wl),
            repScheme: weekRepScheme ?? undefined,
            incrementRuleStS: {
              type: "repScheme",
              value: 0,
              soglie: toRepsSoglie(rule.positiveThresholdsStS),
            },
            decrementRuleStS: {
              type: "repScheme",
              value: 0,
              soglie: toRepsSoglie(rule.negativeThresholdsStS),
            },
          }
        : {
            intensityType: "%",
            progressionType: "set-in-set",
            calculationPriority: "soglie",
            workLoad: String(wl),
            repScheme: weekRepScheme ?? undefined,
            incrementRuleSiS: {
              type: "repScheme",
              value: 0,
              soglie: toRepsSoglie(rule.positiveThresholdsSiS),
            },
            decrementRuleSiS: {
              type: "repScheme",
              value: 0,
              soglie: toRepsSoglie(rule.negativeThresholdsSiS),
            },
          };
    let load: number | undefined;
    let loadLabel: string | undefined;
    let reps: number | string | undefined;
    let repsLabel: string | undefined;
    if (isStS) {
      if (stsTarget === "load" || stsTarget === "both") {
        const v = computeNextWeekLoad(mkLoadSynth("stsLoad"), wr);
        if (v !== undefined && load === undefined) {
          load = v;
          loadLabel = "Sett. succ.";
        }
      }
      if (stsTarget === "reps" || stsTarget === "both") {
        const r = computeNextWeekReps(mkRepsSynth("stsReps"), wr);
        if (r && reps === undefined) {
          reps = r.kind === "scheme" ? r.repScheme : r.reps;
          repsLabel = "Sett. succ.";
        }
      }
    }
    if (isSiS) {
      if (sisTarget === "load" || sisTarget === "both") {
        const v = computeNextSetInSetLoad(mkLoadSynth("sisLoad"), wr);
        if (v !== undefined && load === undefined) {
          load = v;
          loadLabel = "Set succ.";
        }
      }
      if (sisTarget === "reps" || sisTarget === "both") {
        const r = computeNextSetInSetReps(mkRepsSynth("sisReps"), wr);
        if (r && reps === undefined) {
          reps = r.kind === "scheme" ? r.repScheme : r.reps;
          repsLabel = "Set succ.";
        }
      }
    }
    if (load === undefined && reps === undefined) return undefined;
    return { load, loadLabel, reps, repsLabel };
  })();

  // PROG-DETAIL-UX/1 — per-set editable fields rendered as chips (matching
  // the per-week header chip pattern). Trainer-only and athlete-only gating
  // continues to flow through canEditField; for fields that are also display-
  // only when empty (e.g. trialLoad with no value, but viewer can't edit),
  // renderChip returns null. miniSets is special — the chip just hosts the
  // pencil; the projection list still renders in the body below.
  const setChips: ReactNode[] = [
    renderChip(
      "label",
      "Etichetta",
      set.label ?? "senza etichetta",
      set.label == null || set.label === "",
    ),
    renderChip(
      "progressionRule",
      "Regola",
      rl ?? "—",
      rl == null,
    ),
    renderChip(
      "repSchemeTypeId",
      "Tipo schema reps",
      set.repSchemeTypeId != null
        ? (options?.repSchemeTypeOptions?.find((o) => o.value === set.repSchemeTypeId)?.label ?? String(set.repSchemeTypeId))
        : "—",
      set.repSchemeTypeId == null,
    ),
    renderChip(
      "repScheme",
      "Schema reps",
      set.repScheme ?? "—",
      set.repScheme == null || set.repScheme === "",
      "scheme",
    ),
    renderChip(
      "workReps",
      "Work Reps",
      set.workReps != null ? String(set.workReps) : "—",
      set.workReps == null,
      "reps",
    ),
    renderChip(
      "workLoad",
      "Work Load",
      set.workLoad != null ? `${set.workLoad} kg` : "—",
      set.workLoad == null,
      "load",
    ),
    // Carico Suggerito + Mini-set surfaced right after Work Load (UX request).
    renderChip(
      "trialLoad",
      "Carico Suggerito",
      set.trialLoad != null ? `${set.trialLoad} kg` : "—",
      set.trialLoad == null,
      "ref",
    ),
    (set.miniSets && set.miniSets.length > 0) ||
    (effIncludeXReps && onEditSetField && canEditField("miniSets"))
      ? (() => {
          const editable =
            effIncludeXReps && !!onEditSetField && canEditField("miniSets");
          const count = set.miniSets?.length ?? 0;
          return (
            <span key="miniSets" className="ppd-week-chip">
              <strong style={{ marginRight: 4 }}>Mini-set</strong>
              <span style={count === 0 ? { opacity: 0.5 } : undefined}>
                {count === 0 ? "nessuno" : `${count}`}
              </span>
              {editable && (
                <EditPencil
                  onClick={() => onEditSetField!("miniSets", set)}
                  title="Modifica mini-set"
                />
              )}
            </span>
          );
        })()
      : null,
    renderChip(
      "tut",
      "TUT",
      set.tut != null && set.tut !== ""
        ? (options?.tutOptions?.find((o) => String(o.value) === String(set.tut))?.label ?? set.tut)
        : "—",
      set.tut == null || set.tut === "",
    ),
    renderChip(
      "tecnica",
      "Tecnica",
      set.tecnica ?? "—",
      set.tecnica == null || set.tecnica === "",
    ),
    // T2.B (#eda532b5) — Recupero chip. Reads `set.rec` and falls back to the
    // week-level `weekRec` (BE normalizer already copies week → set on read,
    // so the fallback is defensive). Hidden when both are null per UX REV2 §5.
    (() => {
      const recValue = set.rec ?? weekRec ?? null;
      const formatted = formatRec(recValue, options?.recOptions);
      return renderChip(
        "rec",
        "Recupero",
        formatted ?? "—",
        formatted == null,
      );
    })(),
    renderChip(
      "effortTypeId",
      "Tipo sforzo",
      set.effortTypeId != null
        ? (options?.effortTypeOptions?.find((o) => o.value === set.effortTypeId)?.label ?? String(set.effortTypeId))
        : "—",
      set.effortTypeId == null,
    ),
    renderChip(
      "effort",
      "Sforzo",
      set.effort ?? "—",
      set.effort == null || set.effort === "",
    ),
    renderChip(
      "note",
      "Nota",
      set.note ?? "—",
      set.note == null || set.note === "",
    ),
    // Media chip — like renderChip but with an extra "preview" eye that opens
    // the image/video full-screen WITHOUT going through the edit modal.
    (() => {
      const hasMedia = !!(set.imageUrl || set.videoUrl);
      const editable = !!onEditSetField && canEditField("media");
      if (!hasMedia && !editable) return null;
      return (
        <InfoChip
          key="media"
          name="Media"
          value={
            set.imageUrl
              ? "Immagine allegata"
              : set.videoUrl
              ? "Video allegato"
              : "—"
          }
          empty={!hasMedia}
          editable={editable}
          onEdit={() => onEditSetField!("media", set)}
          isMobile={isMobile}
          extraAction={
            hasMedia ? (
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  openMediaPreview(
                    set.imageUrl
                      ? { kind: "image", src: set.imageUrl }
                      : { kind: "video", src: set.videoUrl! },
                  );
                }}
                title="Anteprima media"
                aria-label="Anteprima media"
                style={{ marginLeft: 4, opacity: 0.85 }}
              />
            ) : undefined
          }
        />
      );
    })(),
  ].filter(Boolean);

  // T2.B (#eda532b5) — when rendered as Programmazione Detail (displayMode
  // 'detail'), restrict the chip surface to the architect rev3 whitelist:
  // Rep Scheme · Tecnica · Carico Suggerito (trialLoad) · Work Load · Work Reps
  // · Note · Media · Mini Set. `rec` (Recupero) chip lands with T2.BE; all
  // other chips (label, repSchemeTypeId, effortTypeId, effort, tut,
  // workXReps, workXLoad, progressionRule / rule label) are dropped from the
  // reduced card. Soglie remains as the in-header rule chip and is gated by
  // `isTrainer` below.
  const detailChipFields = new Set<EditableField>([
    "repSchemeTypeId",
    "repScheme",
    "tecnica",
    "effortTypeId",
    "effort",
    "tut",
    "rec",
    "trialLoad",
    "workReps",
    "workLoad",
    "note",
    "media",
    "miniSets",
  ]);
  const filteredSetChips =
    displayMode === "detail"
      ? setChips.filter((chip) => {
          const key = (chip as { key?: EditableField })?.key;
          return key != null && detailChipFields.has(key);
        })
      : setChips;

  // PROG-ENGINE-V3/5 — "Sorgente carico" discoverability chip(s) on the set
  // card header. Shows which slot is currently feeding this set's load per the
  // active loadComputeSource (honors the V3/2 pin when set). Hidden for first
  // week + first set, for fixed/dynamic/null sources, and when no source slot
  // resolves on the given axis.
  const buildSourceChip = (axis: "sts" | "sis"): string | null => {
    const resolved = resolveSourceSlot(weeks, wi, setIndex, axis, rule);
    if (!resolved) return null;
    const axisShort = axis === "sts" ? "StS" : "SiS";
    const anchor = axis === "sts"
      ? `Settimana ${resolved.sourceWi + 1} · Set ${resolved.sourceSi + 1}`
      : `Set ${resolved.sourceSi + 1}`;
    return `← ${anchor} · ${axisShort}`;
  };
  const sourceChips: string[] = !showSourcePreview
    ? []
    : src === "both"
    ? ([buildSourceChip("sts"), buildSourceChip("sis")].filter(
        (s): s is string => !!s,
      ))
    : [buildSourceChip(src as "sts" | "sis")].filter(
        (s): s is string => !!s,
      );

  // T2.B (#eda532b5) — Soglie/rule chip + sourceChips are trainer-only in
  // detail mode (UX REV2 §3 — client-side isTrainer gate). Editor mode keeps
  // the legacy behavior (always shown).
  const showRuleAndSourceChips = displayMode !== "detail" || isTrainer;

  // Same compact "show first few + (+N / meno)" treatment as the week header.
  const SET_COMPACT_CHIPS = 6;
  const setHiddenChipCount = Math.max(
    0,
    filteredSetChips.length - SET_COMPACT_CHIPS,
  );
  const visibleSetChips = setChipsOpen
    ? filteredSetChips
    : filteredSetChips.slice(0, SET_COMPACT_CHIPS);

  return (
    <div className="ppd-set-card">
      <div className="ppd-set-header">
        <span className="ppd-set-num">{setIndex + 1}</span>
        {showRuleAndSourceChips && rl && (
          <span className="ppd-set-rule-chip">{rl}</span>
        )}
        {showRuleAndSourceChips &&
          sourceChips.map((c) => (
            <span key={c} className="ppd-set-rule-chip" title={c}>
              {c}
            </span>
          ))}
      </div>

      {filteredSetChips.length > 0 && (
        <div className="ppd-week-chips" style={{ padding: "8px 12px 0" }}>
          {visibleSetChips}
          {setHiddenChipCount > 0 && (
            <span
              className="ppd-week-chip ppd-week-chip--more"
              role="button"
              tabIndex={0}
              onClick={() => setSetChipsOpen((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSetChipsOpen((v) => !v);
                }
              }}
              title={setChipsOpen ? "Mostra meno" : "Mostra tutte le info"}
            >
              {setChipsOpen ? "− meno" : `+${setHiddenChipCount}`}
            </span>
          )}
        </div>
      )}

      {/* Per-set actions: the primary "Regola soglie" opens the rule editor;
          copy/paste of rule + whole set are consolidated into a "⋯" overflow
          menu so the data matrix stays uncluttered. Trainer-only. */}
      {onEditSetField && canEditField("progressionRule") && (
        <div className="ppd-rule-actions">
          <button
            type="button"
            className="ppd-rule-action-btn ppd-rule-action-btn--primary"
            onClick={() => onEditSetField("progressionRule", set)}
            title="Apri l'editor della regola di progressione"
          >
            <FontAwesomeIcon icon={faSliders} /> Regola soglie StS/SiS
          </button>
          {(() => {
            const items: MenuProps["items"] = [];
            if (onCopyRule)
              items.push({ key: "copyRule", icon: <FontAwesomeIcon icon={faCopy} />, label: "Copia regola", onClick: onCopyRule });
            if (onPasteRule)
              items.push({ key: "pasteRule", icon: <FontAwesomeIcon icon={faPaste} />, label: "Incolla regola", disabled: !canPasteRule, onClick: onPasteRule });
            if ((onCopyRule || onPasteRule) && (onCopySet || onPasteSet))
              items.push({ type: "divider" });
            if (onCopySet)
              items.push({ key: "copySet", icon: <FontAwesomeIcon icon={faCopy} />, label: "Copia set", onClick: onCopySet });
            if (onPasteSet)
              items.push({ key: "pasteSet", icon: <FontAwesomeIcon icon={faPaste} />, label: "Incolla set", disabled: !canPasteSet, onClick: onPasteSet });
            if (items.length === 0) return null;
            return (
              <Dropdown trigger={["click"]} menu={{ items }} placement="bottomRight">
                <button
                  type="button"
                  className="ppd-rule-action-btn ppd-rule-action-btn--icon"
                  title="Altre azioni (copia / incolla)"
                  aria-label="Altre azioni"
                >
                  <FontAwesomeIcon icon={faEllipsis} />
                </button>
              </Dropdown>
            );
          })()}
        </div>
      )}

      <div className="ppd-set-body">
        {/* Mini-set dropdown — posizionato in cima al corpo, così compare
            subito dopo i chip (Work Load è l'ultimo chip work-data). */}
        {((set.miniSets && set.miniSets.length > 0) ||
          (displayMode !== "detail" &&
            effIncludeXReps &&
            onEditSetField &&
            canEditField("miniSets"))) &&
          (() => {
            const miniSets = set.miniSets ?? [];
            const baseLoad =
              (set.workLoad != null ? Number(set.workLoad) : 0) ||
              (set.trialLoad != null ? Number(set.trialLoad) : 0);
            const projections = computeMiniSetLoads(baseLoad, miniSets);
            const listBody =
              miniSets.length === 0 ? (
                <span
                  className="ppd-set-field-value"
                  style={{ opacity: 0.6 }}
                >
                  Nessun mini-set
                </span>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  {miniSets.map((m, i) => {
                    const proj = projections[i];
                    const deltaSign =
                      m.deltaType === "%"
                        ? `${m.deltaValue}%`
                        : `${m.deltaValue} kg`;
                    const hasWork =
                      m.workReps != null || m.workLoad != null;
                    return (
                      <span key={i} className="ppd-set-field-value">
                        Mini #{i + 1}: {String(m.reps)} reps
                        {proj ? ` × ${proj.load} kg` : ""} (-{deltaSign})
                        {hasWork && (
                          <strong style={{ marginLeft: 6 }}>
                            → eseguito:{" "}
                            {m.workReps != null ? `${m.workReps} reps` : "—"}
                            {m.workLoad != null ? ` × ${m.workLoad} kg` : ""}
                          </strong>
                        )}
                      </span>
                    );
                  })}
                </div>
              );
            if (displayMode === "detail") {
              return (
                <Collapse
                  ghost
                  className="ppd-mini-set-collapse"
                  items={[
                    {
                      key: "miniSets",
                      label: `Mini Set (${miniSets.length})`,
                      children: listBody,
                    },
                  ]}
                />
              );
            }
            return (
              <div className="ppd-set-field ppd-set-field--stacked">
                <span className="ppd-set-field-label">
                  Mini-set (drop set)
                </span>
                {listBody}
              </div>
            );
          })()}
        {(hypLoad != null ||
          (showSourcePreview &&
            (sourcePreviewLoad != null || sourcePreviewReps != null))) && (
          <div className="ppd-hyp-group">
            {hypLoad != null && (
              <div className="ppd-set-field">
                <span className="ppd-set-field-label">
                  Carico ipotetico
                  <span className="ppd-set-field-sublabel">(template)</span>
                </span>
                <span className="ppd-set-field-value ppd-hyp-load">
                  {hypLoad} kg
                </span>
              </div>
            )}
            {showSourcePreview &&
              (sourcePreviewLoad != null || sourcePreviewReps != null) && (
                <div className="ppd-set-field ppd-set-field--stacked">
                  <span className="ppd-set-field-label">
                    Ipotetico
                    <span className="ppd-set-field-sublabel">(da sorgente)</span>
                  </span>
                  <div className="pp-source-context">
                    {src === "sts" && prevWeekHypLoad != null && (
                      <>Sett. prec.: {prevWeekHypLoad} kg</>
                    )}
                    {src === "sis" && prevSetHypLoad != null && (
                      <>Set prec.: {prevSetHypLoad} kg</>
                    )}
                    {src === "both" && (
                      <>
                        {prevWeekHypLoad != null && (
                          <>Sett. prec.: {prevWeekHypLoad} kg</>
                        )}
                        {prevWeekHypLoad != null &&
                          prevSetHypLoad != null &&
                          " · "}
                        {prevSetHypLoad != null && (
                          <>Set prec.: {prevSetHypLoad} kg</>
                        )}
                      </>
                    )}
                  </div>
                  <div className="pp-calc-badges">
                    {sourcePreviewLoad != null && (
                      <span className="pp-calc-badge pp-calc-badge-load">
                        {sourcePreviewLoad} kg
                      </span>
                    )}
                    {sourcePreviewReps != null && (
                      <span className="pp-calc-badge pp-calc-badge-reps">
                        {sourcePreviewReps} reps
                      </span>
                    )}
                  </div>
                </div>
              )}
          </div>
        )}
        {/* T2.B (#eda532b5) — in detail mode the Reps Ipotetiche row gets the
            `ppd-hyp-group-member` class so CSS can visually group it with the
            Carico Ipotetico block above and the Catena Ipotetici block below
            (UX REV2 §2 — Carico Ipot. + Reps Ipot. + Catena Ipot. read as one
            block in the reduced detail card). */}
        {hypReps != null && (
          <div
            className={
              displayMode === "detail"
                ? "ppd-set-field ppd-hyp-group-member"
                : "ppd-set-field"
            }
          >
            <span className="ppd-set-field-label">Reps ipotetiche</span>
            <span className="ppd-set-field-value ppd-hyp-reps">{hypReps}</span>
          </div>
        )}
        {/* PROG-DETAIL-UX/1 — editable rows (workReps, workLoad, workXReps,
            workXLoad, note, media) moved into the per-set chips block above. */}
        {/* T2.B (#eda532b5) — workFuturo + rule-meta are not on the detail
            whitelist. In editor mode keep legacy behavior; in detail mode
            always hide workFuturo, and gate rule-meta by `isTrainer`. */}
        {displayMode !== "detail" && workFuturo?.load !== undefined && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label ppd-set-field-label--futuro">
              Carico ipotetico futuro (da work data) — {workFuturo.loadLabel}
            </span>
            <span className="ppd-set-field-value ppd-hyp-load">
              {workFuturo.load} kg
            </span>
          </div>
        )}
        {displayMode !== "detail" && workFuturo?.reps !== undefined && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label ppd-set-field-label--futuro">
              Reps ipotetiche future (da work data) — {workFuturo.repsLabel}
            </span>
            <span className="ppd-set-field-value ppd-hyp-load">
              {workFuturo.reps} reps
            </span>
          </div>
        )}
        {(displayMode !== "detail" || isTrainer) &&
          (rule.progressionType === "set-to-set" ||
            rule.progressionType === "both") &&
          rule.type && (
            <div className="ppd-set-field">
              <span className="ppd-set-field-label">Set to Set</span>
              <span className="ppd-set-field-value">
                {rule.type === "thresholds" ? "Soglie" : "Fissa"}
              </span>
            </div>
          )}
        {(displayMode !== "detail" || isTrainer) &&
          (rule.progressionType === "set-in-set" ||
            rule.progressionType === "both") &&
          (rule.typeSiS ?? rule.type) && (
            <div className="ppd-set-field">
              <span className="ppd-set-field-label">Set in Set</span>
              <span className="ppd-set-field-value">
                {(rule.typeSiS ?? rule.type) === "thresholds"
                  ? "Soglie"
                  : "Fissa"}
              </span>
            </div>
          )}
        {(displayMode !== "detail" || isTrainer) &&
          rule.applyForNextWeeks != null && (
            <div className="ppd-set-field">
              <span className="ppd-set-field-label">
                Applica per (sett. successive)
              </span>
              <span className="ppd-set-field-value">
                {rule.applyForNextWeeks}
              </span>
            </div>
          )}
        {(displayMode !== "detail" || isTrainer) &&
          rule.applyForNextSets != null && (
            <div className="ppd-set-field">
              <span className="ppd-set-field-label">
                Applica per (set successivi)
              </span>
              <span className="ppd-set-field-value">
                {rule.applyForNextSets}
              </span>
            </div>
          )}
        {/* #972f8e54 — pass real `weeks` + `(wi, setIndex)` so simulateChain's
            V3/1 walk-back at precompiledChain.ts:147-167 can resolve a
            dependent set's source. The legacy 1-week stub starved walk-back
            and silently rendered an empty chain on dependents whose rule
            body only carried a dispatch hint. */}
        {(() => {
          const chain = simulateChain(weeks, wi, setIndex, {
            tecnicheRaw: options?.tecnicheRaw ?? [],
          });
          if (chain.length === 0) return null;
          const groups: Record<"main" | "xReps" | "miniSet", typeof chain> = {
            main: [],
            xReps: [],
            miniSet: [],
          };
          chain.forEach((c) => groups[c.kind].push(c));
          const mainLoad = groups.main.filter((c) => c.load != null);
          const mainReps = groups.main.filter((c) => c.reps != null);
          return (
            <div
              className={
                displayMode === "detail"
                  ? "ppd-set-field ppd-set-field--stacked ppd-hyp-group-member"
                  : "ppd-set-field ppd-set-field--stacked"
              }
            >
              <span className="ppd-set-field-label">Catena ipotetici</span>
              {groups.main.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    width: "100%",
                  }}
                >
                  {mainLoad.length > 0 && (
                    <div className="pp-chain-row">
                      <span
                        className="pp-chain-row-label"
                        style={{ minWidth: 44 }}
                      >
                        Carico
                      </span>
                      {mainLoad.map((c, i) => (
                        <span
                          key={`main-load-${i}`}
                          className="pp-calc-badge pp-calc-badge-load pp-calc-badge--sm"
                        >
                          {c.label}: {c.load} kg
                        </span>
                      ))}
                    </div>
                  )}
                  {mainReps.length > 0 && (
                    <div className="pp-chain-row">
                      <span
                        className="pp-chain-row-label"
                        style={{ minWidth: 44 }}
                      >
                        Reps
                      </span>
                      {mainReps.map((c, i) => (
                        <span
                          key={`main-reps-${i}`}
                          className="pp-calc-badge pp-calc-badge-reps pp-calc-badge--sm"
                        >
                          {c.label.replace(" (reps)", "")} × {c.reps} reps
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
        {!hasHyp &&
          set.trialLoad == null &&
          !hasWorkData &&
          !(set.miniSets && set.miniSets.length > 0) && (
            <span className="ppd-set-empty">Nessun dato ipotetico</span>
          )}
      </div>

      <Modal
        open={!!mediaPreview}
        onCancel={() => setMediaPreview(null)}
        footer={null}
        destroyOnClose
        title="Anteprima media"
        width={mediaPreview?.kind === "video" ? 720 : 520}
        data-testid="set-media-preview-modal"
      >
        {mediaPreview?.kind === "image" && (
          <Image
            src={mediaPreview.src}
            alt="Media allegato"
            style={{ width: "100%" }}
            preview={false}
          />
        )}
        {mediaPreview?.kind === "video" && (
          <video
            key={mediaPreviewNonce}
            src={mediaPreview.src}
            controls
            autoPlay
            style={{ width: "100%", maxHeight: "70vh", background: "#000" }}
          />
        )}
      </Modal>
    </div>
  );
}

// ─── WeekDetailCard ───────────────────────────────────────────────────────────

interface WeekDetailCardProps {
  week: PrecompiledWeek;
  index: number;
  includeXReps: boolean;
  /** PROG-DETAIL-NEW/5 — tecnica.dropXReps signal for the miniSets pencil. */
  dropXReps: boolean;
  hypLoads: (number | null)[];
  hypReps: (number | string | null)[];
  prevWeekSets: PrecompiledSet[] | null;
  prevWeekHypLoads: (number | null)[] | null;
  /** PROG-ENGINE-V3/5 — passed through to SetDetailCard for the source chip. */
  weeks: PrecompiledWeek[];
  /** Catalog options for resolving repSchemeTypeId / effortTypeId names. */
  options?: ProgEditorOptions;
  onEditWeekField:
    | ((field: EditableField) => void)
    | null;
  onEditSetField:
    | ((field: EditableField, setIndex: number) => void)
    | null;
  /** Whole-rule Copia/Incolla per set (programmazione-local). */
  onCopySetRule: ((setIndex: number) => void) | null;
  onPasteSetRule: ((setIndex: number) => void) | null;
  canPasteRule: boolean;
  /** Whole-set Copia/Incolla per set (programmazione-local). */
  onCopySet: ((setIndex: number) => void) | null;
  onPasteSet: ((setIndex: number) => void) | null;
  canPasteSet: boolean;
  /** Whole-week Copia/Incolla for this week (programmazione-local). */
  onCopyWeek: (() => void) | null;
  onPasteWeek: (() => void) | null;
  canPasteWeek: boolean;
  /** Set/clear this week's calendar date (null = read-only display). */
  onSetWeekDate: ((iso: string | null) => void) | null;
  /** Opens the "Aggiungi set" rule modal for this week (null = not editable). */
  onAddSet: (() => void) | null;
  canEditField: (field: EditableField) => boolean;
  /** T2.B (#eda532b5) — forwarded to SetDetailCard. */
  displayMode: "detail" | "edit";
  /** T2.B (#eda532b5) — forwarded to SetDetailCard for the rule-meta gate. */
  isTrainer: boolean;
}

function WeekDetailCard({
  week,
  index,
  includeXReps,
  dropXReps,
  hypLoads,
  hypReps,
  prevWeekSets,
  prevWeekHypLoads,
  weeks,
  options,
  onEditWeekField,
  onEditSetField,
  onCopySetRule,
  onPasteSetRule,
  canPasteRule,
  onCopySet,
  onPasteSet,
  canPasteSet,
  onCopyWeek,
  onPasteWeek,
  canPasteWeek,
  onSetWeekDate,
  onAddSet,
  canEditField,
  displayMode,
  isTrainer,
}: WeekDetailCardProps) {
  const isFirstWeek = index === 0;
  const sets = week.sets ?? [];

  // PROG-DETAIL-NEW/3 — week-level chips with optional pencils. Render the
  // chip even when the underlying value is null so the trainer always has an
  // edit surface (Q1: trainer = all pencils). Each chip shows the field name
  // and its value ("Nome campo: valore") so the header is self-describing;
  // catalog-backed values (tecnica / tut) are resolved id→name via the
  // ProgEditorOptions catalogs.
  const resolveFromOptions = (
    opts: { label: string; value: string | number }[] | undefined,
    raw: string | number | null | undefined,
  ): string | null => {
    if (raw == null || raw === "") return null;
    const hit = opts?.find((o) => String(o.value) === String(raw));
    return hit?.label ?? String(raw);
  };

  type WeekChip = {
    key: string;
    name: string;
    value: string | null;
    field: EditableField;
  };
  // Order (fixed): Tipo Schema Reps · Rep Scheme · Tecnica · Tipo Sforzo ·
  // Sforzo · TUT · Recupero. type-id chips (id→name resolved) drive the
  // cascading repScheme / effort options.
  const chips: WeekChip[] = [];
  if (
    week.repSchemeTypeId != null ||
    (onEditWeekField && canEditField("week:repSchemeTypeId"))
  ) {
    chips.push({
      key: "repSchemeTypeId",
      name: "Tipo Schema Reps",
      value: resolveFromOptions(options?.repSchemeTypeOptions, week.repSchemeTypeId),
      field: "week:repSchemeTypeId",
    });
  }
  if (week.repScheme || (onEditWeekField && canEditField("week:repScheme"))) {
    chips.push({
      key: "repScheme",
      name: "Rep Scheme",
      value: week.repScheme || null,
      field: "week:repScheme",
    });
  }
  if (week.tecnica || (onEditWeekField && canEditField("week:tecnica"))) {
    chips.push({
      key: "tecnica",
      name: "Tecnica",
      value: resolveFromOptions(options?.tecnicaOptions, week.tecnica),
      field: "week:tecnica",
    });
  }
  if (
    week.effortTypeId != null ||
    (onEditWeekField && canEditField("week:effortTypeId"))
  ) {
    chips.push({
      key: "effortTypeId",
      name: "Tipo Sforzo",
      value: resolveFromOptions(options?.effortTypeOptions, week.effortTypeId),
      field: "week:effortTypeId",
    });
  }
  if (week.effort || (onEditWeekField && canEditField("week:effort"))) {
    chips.push({
      key: "effort",
      name: "Sforzo",
      value: week.effort || null,
      field: "week:effort",
    });
  }
  if (week.tut || (onEditWeekField && canEditField("week:tut"))) {
    chips.push({
      key: "tut",
      name: "TUT",
      value: resolveFromOptions(options?.tutOptions, week.tut),
      field: "week:tut",
    });
  }
  if (week.rec || (onEditWeekField && canEditField("week:rec"))) {
    chips.push({
      key: "rec",
      name: "Recupero",
      value: resolveFromOptions(options?.recOptions, week.rec),
      field: "week:rec",
    });
  }

  // Q4: v1 = always open week 1 only.
  const [open, setOpen] = useState<boolean>(isFirstWeek);
  // Week-header chips are dense (up to 7). Show the first few inline and tuck
  // the rest behind a "+N / meno" toggle so the header stays compact.
  const [chipsOpen, setChipsOpen] = useState(false);
  const weekScreens = Grid.useBreakpoint();
  const isMobile = !weekScreens.md;
  const COMPACT_CHIPS = 4;
  const hiddenChipCount = Math.max(0, chips.length - COMPACT_CHIPS);
  const visibleChips = chipsOpen ? chips : chips.slice(0, COMPACT_CHIPS);

  return (
    <div className={`ppd-week-card${open ? " open" : ""}`}>
      <button
        type="button"
        className="ppd-week-header"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
      >
        <span className="ppd-week-num">{index + 1}</span>
        <div className="ppd-week-info">
          <span className="ppd-week-title">Settimana {index + 1}</span>
          {chips.length > 0 && (
            <div className="ppd-week-chips">
              {visibleChips.map((c) => (
                <InfoChip
                  key={c.key}
                  name={c.name}
                  value={c.value ?? "—"}
                  empty={c.value == null}
                  editable={!!onEditWeekField && canEditField(c.field)}
                  onEdit={() => onEditWeekField?.(c.field)}
                  isMobile={isMobile}
                  stopToggle
                />
              ))}
              {hiddenChipCount > 0 && (
                <span
                  className="ppd-week-chip ppd-week-chip--more"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setChipsOpen((v) => !v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      e.preventDefault();
                      setChipsOpen((v) => !v);
                    }
                  }}
                  title={chipsOpen ? "Mostra meno" : "Mostra tutte le info"}
                >
                  {chipsOpen ? "− meno" : `+${hiddenChipCount}`}
                </span>
              )}
            </div>
          )}
        </div>
        <span className="ppd-week-set-count">{sets.length} set</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`ppd-week-chevron${open ? " open" : ""}`}
        />
      </button>

      {/* Whole-week Copia/Incolla — consolidated into a "⋯" menu (outside the
          header toggle button to avoid nested buttons). Trainer-only. */}
      {(onCopyWeek || onPasteWeek) && (
        <div className="ppd-rule-actions ppd-week-actions">
          {(() => {
            const items: MenuProps["items"] = [];
            if (onCopyWeek)
              items.push({ key: "copyWeek", icon: <FontAwesomeIcon icon={faCopy} />, label: "Copia settimana", onClick: onCopyWeek });
            if (onPasteWeek)
              items.push({ key: "pasteWeek", icon: <FontAwesomeIcon icon={faPaste} />, label: "Incolla settimana", disabled: !canPasteWeek, onClick: onPasteWeek });
            return (
              <Dropdown trigger={["click"]} menu={{ items }} placement="bottomRight">
                <button
                  type="button"
                  className="ppd-rule-action-btn ppd-rule-action-btn--icon"
                  title="Azioni settimana (copia / incolla)"
                  aria-label="Azioni settimana"
                >
                  <FontAwesomeIcon icon={faEllipsis} /> Settimana
                </button>
              </Dropdown>
            );
          })()}
        </div>
      )}

      {/* Per-week calendar date + "Aggiungi al calendario". Editable when the
          trainer can edit; otherwise a read-only date + export for the athlete. */}
      {(week.date || onSetWeekDate) && (
        <div className="ppd-week-date-row" style={{ padding: "0 12px 8px" }}>
          <WeekDateControl
            weekNumber={index + 1}
            date={week.date}
            onChange={onSetWeekDate ?? undefined}
          />
        </div>
      )}

      {open && (
        <>
          {sets.length > 0 && (
            <div className="ppd-sets-grid">
              {sets.map((s, si) => (
                <SetDetailCard
                  key={si}
                  set={s}
                  setIndex={si}
                  isFirstWeek={isFirstWeek}
                  weekRepScheme={week.repScheme}
                  weekRec={week.rec ?? null}
                  includeXReps={includeXReps}
                  dropXReps={dropXReps}
                  hypLoad={hypLoads[si] ?? null}
                  hypReps={hypReps[si] ?? null}
                  prevWeekSet={prevWeekSets?.[si] ?? null}
                  prevSetInWeek={si > 0 ? sets[si - 1] ?? null : null}
                  prevWeekHypLoad={prevWeekHypLoads?.[si] ?? null}
                  prevSetHypLoad={si > 0 ? hypLoads[si - 1] ?? null : null}
                  weeks={weeks}
                  wi={index}
                  options={options}
                  onEditSetField={
                    onEditSetField
                      ? (field, _set) => onEditSetField(field, si)
                      : null
                  }
                  onCopyRule={onCopySetRule ? () => onCopySetRule(si) : undefined}
                  onPasteRule={onPasteSetRule ? () => onPasteSetRule(si) : undefined}
                  canPasteRule={canPasteRule}
                  onCopySet={onCopySet ? () => onCopySet(si) : undefined}
                  onPasteSet={onPasteSet ? () => onPasteSet(si) : undefined}
                  canPasteSet={canPasteSet}
                  canEditField={canEditField}
                  displayMode={displayMode}
                  isTrainer={isTrainer}
                />
              ))}
            </div>
          )}
          {sets.length === 0 && (
            <div className="ppd-no-sets">
              <FontAwesomeIcon icon={faDumbbell} style={{ opacity: 0.3 }} />
              <span>Nessun set configurato</span>
            </div>
          )}
          {onAddSet && (
            <div className="ppd-add-set-row">
              <button
                type="button"
                className="ppd-add-set-btn"
                onClick={onAddSet}
              >
                <FontAwesomeIcon icon={faPlus} /> Aggiungi set
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface ProgrammazioneDetailEditorProps {
  value: ExerciseProgression;
  /** Catalog options threaded by the Detail page. DETAIL-NEW/3 reads
   *  tutOptions / tecnicaOptions / tecnicheRaw + the cascading
   *  `loadRepSchemesByType` / `loadEffortsByType` fetchers when opening
   *  the InlineEditModal for select / hybrid fields. */
  options?: ProgEditorOptions;
  /** PROG-DETAIL-NEW/3 — position inside the parent Programmazione, needed
   *  to address the right week/set when calling the page-level mutations.
   *  Optional so legacy/read-only consumers keep compiling. */
  splitIndex?: number;
  exerciseIndex?: number;
  isTrainer?: boolean;
  isAthlete?: boolean;
  mutations?: ReturnType<typeof useProgrammazioneMutations>;
  /** T2.B (#eda532b5) — `detail` renders the reduced field surface per UX
   *  REV2 (architect rev3 mapping): chip whitelist filter, isTrainer gate on
   *  Soglie/rule-meta, miniSets in AntD <Collapse>. `edit` (default) keeps
   *  the legacy editor surface. */
  displayMode?: "detail" | "edit";
}

// PROG-DETAIL-NEW/3 — infer the mode toggle for tut / tecnica from the saved
// hybrid string. If it matches a catalog id, the user picked from the select;
// otherwise treat it as free-text.
function inferModeFromOptions(
  value: string | null,
  opts: { label: string; value: string | number }[],
): "select" | "free" {
  if (value == null || value === "") return "select";
  const match = opts.some((o) => String(o.value) === value);
  return match ? "select" : "free";
}

// Empty progression rule for a freshly-added set (mirrors the form's emptyRule).
function emptyRule(): PrecompiledWeekProgressionRule {
  return {
    type: "fixed",
    loadComputeSource: null,
    initialLoadType: "fixed",
    initialLoad: null,
    initialWnRm: null,
    initialPnRm: null,
    weekPriority: null,
    incrementDirection: null,
    incrementTarget: null,
    repsChange: null,
    loadChangeType: null,
    loadChangeValue: null,
    weekWnRm: null,
    weekPnRm: null,
    progressionType: null,
    positiveThresholdsStS: null,
    negativeThresholdsStS: null,
    positiveThresholdsSiS: null,
    negativeThresholdsSiS: null,
    matrixSource: null,
    calculationPriority: null,
    weekPrioritySiS: null,
    incrementDirectionSiS: null,
    incrementTargetSiS: null,
    repsChangeSiS: null,
    loadChangeTypeSiS: null,
    loadChangeValueSiS: null,
    weekWnRmSiS: null,
    weekPnRmSiS: null,
    matrixSourceSiS: null,
  };
}

export default function ProgrammazioneDetailEditor({
  value,
  options,
  splitIndex,
  exerciseIndex,
  isTrainer = false,
  isAthlete = false,
  mutations,
  displayMode = "edit",
}: ProgrammazioneDetailEditorProps) {
  // PROG-DETAIL-NEW/3 — stabilize `weeks` so the new useCallback hooks below
  // (openWeekEdit / openSetEdit / includeXRepsFor) don't re-create on every
  // render whenever `value.weeks` is a fresh array reference but the contents
  // are unchanged.
  const weeks = useMemo(() => value.weeks ?? [], [value.weeks]);

  // Full grids precomputed once so each WeekDetailCard receives the
  // ENGINE-2 walk-back-aware projections without re-deriving them.
  const hypLoadGrid = useMemo(() => computeHypLoadGrid(weeks), [weeks]);
  const hypRepsGrid = useMemo(() => computeHypRepsGrid(weeks), [weeks]);

  // PROG-DETAIL-NEW/3 — role gating helper (Q1 user decision).
  const canEditField = useCallback(
    (field: EditableField): boolean => {
      if (isTrainer) return true;
      if (isAthlete) return ATHLETE_FIELDS.has(field);
      return false;
    },
    [isTrainer, isAthlete],
  );

  // Per-box StS/SiS rule clipboard (shared canonical slot) so the rule editor
  // modal in the detail view supports Copia/Incolla just like the library
  // crea/modifica form.
  const {
    copiedPrecompiledRuleBox,
    copyPrecompiledRuleBox,
    copiedPrecompiledRule,
    copyPrecompiledRule,
    copiedPrecompiledSet,
    copyPrecompiledSet,
    copiedPrecompiledWeek,
    copyPrecompiledWeek,
  } = useClipboard();
  const { showToast, removeToast } = useToasts();
  const { track } = useUploadIndicator();

  // Toast with an inline "Annulla" action so a destructive paste (which
  // overwrites + persists immediately) is recoverable. `undoFn` re-applies the
  // captured prior value through the same mutation channel.
  const showUndoToast = useCallback(
    (id: string, message: string, undoFn: () => void) => {
      showToast(
        id,
        (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            {message}
            <button
              type="button"
              onClick={() => {
                undoFn();
                removeToast(id);
              }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "var(--app-primary)",
                fontWeight: 700,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Annulla
            </button>
          </span>
        ),
        undefined,
        "success",
      );
    },
    [showToast, removeToast],
  );
  const handleCopyBoxConfig = useCallback(
    (config: ProgressionMethodConfig, kind: "sts" | "sis") => {
      copyPrecompiledRuleBox(config);
      showToast(
        "pp-rule-box-copied",
        kind === "sts" ? "Box Set to Set copiato" : "Box Set in Set copiato",
        undefined,
        "success",
      );
    },
    [copyPrecompiledRuleBox, showToast],
  );

  // Modal state + per-week catalog caches for the cascading repScheme/effort
  // selects (hydrated on demand when the pencil opens the modal).
  const [modal, setModal] = useState<ModalConfig | null>(null);
  const [saving, setSaving] = useState(false);
  // PROG-DETAIL-NEW/4 — separate state for the large-modal progressionRule
  // editor (uses AntD Modal + ProgressionRuleEditor, not InlineEditModal).
  // Tracks the focused set coords plus a staging `rule` patched by the
  // editor's `onChange(partial)` callback; committed on Save.
  const [ruleEdit, setRuleEdit] = useState<{
    weekIndex: number;
    setIndex: number;
    rule: PrecompiledWeekProgressionRule;
  } | null>(null);
  const [ruleSaving, setRuleSaving] = useState(false);
  // "Aggiungi set" — staging for a brand-new set's progression rule. The new
  // set lands at index = week.sets.length; committed via addProgressionSet.
  const [addSetEdit, setAddSetEdit] = useState<{
    weekIndex: number;
    rule: PrecompiledWeekProgressionRule;
  } | null>(null);
  const [addSetSaving, setAddSetSaving] = useState(false);
  // PROG-DETAIL-NEW/5 — staging for the miniSets list editor. Local copy so
  // the user can add/remove/edit rows and only commit on OK.
  const [miniSetsEdit, setMiniSetsEdit] = useState<{
    weekIndex: number;
    setIndex: number;
    miniSets: MiniSet[];
  } | null>(null);
  const [miniSetsSaving, setMiniSetsSaving] = useState(false);
  // PROG-DETAIL-NEW/6 — staging for the image/video AllegatiPanel modal.
  // Holds a shallow-cloned PrecompiledSet so File handles persist (JSON
  // clone would strip them) and the AllegatiPanel's `onPatch` calls don't
  // mutate the live programmazione until Save.
  const [mediaEdit, setMediaEdit] = useState<{
    weekIndex: number;
    setIndex: number;
    stagedSet: PrecompiledSet;
  } | null>(null);
  const [repSchemeCache, setRepSchemeCache] = useState<
    Record<number, { label: string; value: string }[]>
  >({});
  const [effortCache, setEffortCache] = useState<
    Record<number, { label: string; value: string }[]>
  >({});

  const wireEdit =
    splitIndex != null && exerciseIndex != null && mutations
      ? (splitIndex as number)
      : null;

  const closeModal = useCallback(() => {
    setModal(null);
    setSaving(false);
  }, []);

  const commit = useCallback(
    async (doMutate: () => Promise<unknown> | unknown) => {
      setSaving(true);
      try {
        await doMutate();
        closeModal();
      } finally {
        setSaving(false);
      }
    },
    [closeModal],
  );

  const ensureRepSchemeOpts = useCallback(
    async (typeId: number | null | undefined) => {
      if (!typeId || !options) return [] as { label: string; value: string }[];
      if (repSchemeCache[typeId]) return repSchemeCache[typeId];
      const opts = await options.loadRepSchemesByType(typeId);
      setRepSchemeCache((prev) => ({ ...prev, [typeId]: opts }));
      return opts;
    },
    [options, repSchemeCache],
  );

  const ensureEffortOpts = useCallback(
    async (typeId: number | null | undefined) => {
      if (!typeId || !options) return [] as { label: string; value: string }[];
      if (effortCache[typeId]) return effortCache[typeId];
      const opts = await options.loadEffortsByType(typeId);
      setEffortCache((prev) => ({ ...prev, [typeId]: opts }));
      return opts;
    },
    [options, effortCache],
  );

  // Per-week tecnica flag lookup (includeXReps + dropXReps). Single resolve
  // per week so SetDetailCard can render workXReps/workXLoad (DETAIL-NEW/3)
  // and the miniSets pencil (DETAIL-NEW/5) without re-walking tecnicheRaw.
  const tecnicheRaw = useMemo(() => options?.tecnicheRaw ?? [], [options]);
  const tecnicaFlagsFor = useCallback(
    (
      week: PrecompiledWeek,
    ): { includeXReps: boolean; dropXReps: boolean } => {
      const t = week.tecnica;
      if (t == null || t === "") return { includeXReps: false, dropXReps: false };
      const s = String(t);
      const match = tecnicheRaw.find(
        (x) => String((x as { id?: number }).id) === s ||
          (x as { name?: string }).name === s,
      );
      const m = match as
        | { includeXReps?: boolean; dropXReps?: boolean }
        | undefined;
      return {
        includeXReps: !!m?.includeXReps,
        dropXReps: !!m?.dropXReps,
      };
    },
    [tecnicheRaw],
  );

  const openWeekEdit = useCallback(
    (weekIndex: number, field: EditableField) => {
      if (wireEdit == null || exerciseIndex == null || !mutations) return;
      const week = weeks[weekIndex];
      if (!week) return;
      const saveWeek = (patch: Partial<PrecompiledWeek>) =>
        commit(() =>
          mutations.updateProgressionWeek(
            splitIndex!,
            exerciseIndex,
            weekIndex,
            patch,
          ),
        );

      switch (field) {
        case "week:repScheme": {
          // Cascading: needs repScheme catalog for the week's repSchemeTypeId.
          const typeId = week.repSchemeTypeId || 0;
          const cached = repSchemeCache[typeId];
          const LBL = "Schema reps (sett.)";
          setModal({
            fieldLabel: LBL,
            fieldType: "hybrid",
            value: week.repScheme ?? "",
            options: cached ?? [],
            optionsLoading: !!typeId && !cached,
            onSave: (v) => saveWeek({ repScheme: v ?? "" }),
          });
          if (typeId && !cached) {
            ensureRepSchemeOpts(typeId).then((opts) =>
              setModal((m) =>
                m && m.fieldLabel === LBL
                  ? { ...m, options: opts, optionsLoading: false }
                  : m,
              ),
            );
          }
          return;
        }
        case "week:effort": {
          const typeId = week.effortTypeId || 0;
          const cached = effortCache[typeId];
          const LBL = "Sforzo (sett.)";
          setModal({
            fieldLabel: LBL,
            fieldType: "hybrid",
            value: week.effort ?? "",
            options: cached ?? [],
            optionsLoading: !!typeId && !cached,
            onSave: (v) => saveWeek({ effort: v ?? "" }),
          });
          if (typeId && !cached) {
            ensureEffortOpts(typeId).then((opts) =>
              setModal((m) =>
                m && m.fieldLabel === LBL
                  ? { ...m, options: opts, optionsLoading: false }
                  : m,
              ),
            );
          }
          return;
        }
        case "week:tut": {
          const opts = options?.tutOptions ?? [];
          setModal({
            fieldLabel: "TUT (sett.)",
            fieldType: "hybrid",
            value: week.tut ?? "",
            options: opts,
            onSave: (v) =>
              saveWeek({
                tut: v ? String(v) : null,
                tutMode: inferModeFromOptions(v ? String(v) : null, opts),
              }),
          });
          return;
        }
        case "week:tecnica": {
          const opts = options?.tecnicaOptions ?? [];
          setModal({
            fieldLabel: "Tecnica (sett.)",
            fieldType: "hybrid",
            value: week.tecnica ?? "",
            options: opts,
            onSave: (v) =>
              saveWeek({
                tecnica: v ? String(v) : null,
                tecnicaMode: inferModeFromOptions(v ? String(v) : null, opts),
                // Reset the X-reps params when the tecnica changes, mirroring
                // the editor's behavior (FE-3 onPatchWeek for tecnica).
                xRepsTarget: null,
                xRepsLoadDropPerc: null,
              }),
          });
          return;
        }
        case "week:rec": {
          // Recupero a livello settimana (hybrid: catalogo recOptions +
          // free-text), mirrors the per-set `rec` case.
          const opts = options?.recOptions ?? [];
          setModal({
            fieldLabel: "Recupero (sett.)",
            fieldType: "hybrid",
            value: week.rec ?? "",
            options: opts,
            onSave: (v) => saveWeek({ rec: v === "" || v == null ? null : String(v) }),
          });
          return;
        }
        case "week:xRepsTarget":
          setModal({
            fieldLabel: "XReps target (sett.)",
            fieldType: "number",
            value: week.xRepsTarget ?? "",
            onSave: (v) =>
              saveWeek({
                xRepsTarget:
                  v === "" || v == null ? null : Number(v),
              }),
          });
          return;
        case "week:xRepsLoadDropPerc":
          setModal({
            fieldLabel: "Drop XReps % (sett.)",
            fieldType: "number",
            value: week.xRepsLoadDropPerc ?? "",
            onSave: (v) =>
              saveWeek({
                xRepsLoadDropPerc:
                  v === "" || v == null ? null : Number(v),
              }),
          });
          return;
        case "week:repSchemeTypeId":
          setModal({
            fieldLabel: "Tipo schema reps (sett.)",
            fieldType: "select",
            value: week.repSchemeTypeId || "",
            options: options?.repSchemeTypeOptions ?? [],
            // Reset the dependent repScheme when the type changes — same
            // contract as the editor's cascading W2 widget.
            onSave: (v) =>
              saveWeek({
                repSchemeTypeId: v === "" || v == null ? 0 : Number(v),
                repScheme: "",
              }),
          });
          return;
        case "week:effortTypeId":
          setModal({
            fieldLabel: "Tipo sforzo (sett.)",
            fieldType: "select",
            value: week.effortTypeId || "",
            options: options?.effortTypeOptions ?? [],
            onSave: (v) =>
              saveWeek({
                effortTypeId: v === "" || v == null ? 0 : Number(v),
                effort: "",
              }),
          });
          return;
        default:
          return;
      }
    },
    [
      wireEdit,
      exerciseIndex,
      mutations,
      weeks,
      splitIndex,
      options,
      repSchemeCache,
      effortCache,
      ensureRepSchemeOpts,
      ensureEffortOpts,
      commit,
    ],
  );

  const openSetEdit = useCallback(
    (weekIndex: number, setIndex: number, field: EditableField) => {
      if (wireEdit == null || exerciseIndex == null || !mutations) return;
      const week = weeks[weekIndex];
      const set = week?.sets?.[setIndex];
      if (!week || !set) return;
      const saveSet = (patch: Partial<PrecompiledSet>) =>
        commit(() =>
          mutations.updateProgressionSet(
            splitIndex!,
            exerciseIndex,
            weekIndex,
            setIndex,
            patch,
          ),
        );

      switch (field) {
        case "workReps":
          setModal({
            fieldLabel: "Work Reps",
            fieldType: "text",
            value: set.workReps ?? "",
            onSave: (v) => saveSet({ workReps: v === "" ? null : v }),
          });
          return;
        case "workLoad":
          setModal({
            fieldLabel: "Work Load",
            fieldType: "number",
            value: set.workLoad ?? "",
            onSave: (v) =>
              saveSet({ workLoad: v === "" || v == null ? null : Number(v) }),
          });
          return;
        case "workXReps":
          setModal({
            fieldLabel: "Work X-Reps",
            fieldType: "text",
            value: set.workXReps ?? "",
            onSave: (v) => saveSet({ workXReps: v === "" ? null : v }),
          });
          return;
        case "workXLoad":
          setModal({
            fieldLabel: "Work X-Load",
            fieldType: "number",
            value: set.workXLoad ?? "",
            onSave: (v) =>
              saveSet({ workXLoad: v === "" || v == null ? null : Number(v) }),
          });
          return;
        case "note":
          setModal({
            fieldLabel: "Nota",
            fieldType: "textarea",
            value: set.note ?? "",
            onSave: (v) => saveSet({ note: v === "" ? null : v }),
          });
          return;
        case "label":
          setModal({
            fieldLabel: "Etichetta",
            fieldType: "text",
            value: set.label ?? "",
            onSave: (v) => saveSet({ label: v === "" ? null : v }),
          });
          return;
        case "trialLoad":
          setModal({
            fieldLabel: "Carico Suggerito",
            fieldType: "number",
            value: set.trialLoad ?? "",
            onSave: (v) =>
              saveSet({ trialLoad: v === "" || v == null ? null : Number(v) }),
          });
          return;
        case "repSchemeTypeId":
          setModal({
            fieldLabel: "Tipo schema reps (set)",
            fieldType: "select",
            value: set.repSchemeTypeId || "",
            options: options?.repSchemeTypeOptions ?? [],
            onSave: (v) =>
              saveSet({
                repSchemeTypeId: v === "" || v == null ? null : Number(v),
                repScheme: null,
              }),
          });
          return;
        case "repScheme": {
          const typeId = set.repSchemeTypeId || week.repSchemeTypeId || 0;
          const cached = repSchemeCache[typeId];
          const LBL = "Schema reps (set)";
          setModal({
            fieldLabel: LBL,
            fieldType: "hybrid",
            value: set.repScheme ?? "",
            options: cached ?? [],
            optionsLoading: !!typeId && !cached,
            onSave: (v) => saveSet({ repScheme: v === "" ? null : String(v) }),
          });
          // Resolve the type's options and patch the OPEN modal when they
          // arrive (otherwise the first open after changing the type would
          // show a never-ending spinner until reopened).
          if (typeId && !cached) {
            ensureRepSchemeOpts(typeId).then((opts) =>
              setModal((m) =>
                m && m.fieldLabel === LBL
                  ? { ...m, options: opts, optionsLoading: false }
                  : m,
              ),
            );
          }
          return;
        }
        case "effortTypeId":
          setModal({
            fieldLabel: "Tipo sforzo (set)",
            fieldType: "select",
            value: set.effortTypeId || "",
            options: options?.effortTypeOptions ?? [],
            onSave: (v) =>
              saveSet({
                effortTypeId: v === "" || v == null ? null : Number(v),
                effort: null,
              }),
          });
          return;
        case "effort": {
          const typeId = set.effortTypeId || week.effortTypeId || 0;
          const cached = effortCache[typeId];
          const LBL = "Sforzo (set)";
          setModal({
            fieldLabel: LBL,
            fieldType: "select",
            value: set.effort ?? "",
            options: cached ?? [],
            optionsLoading: !!typeId && !cached,
            onSave: (v) => saveSet({ effort: v === "" ? null : String(v) }),
          });
          if (typeId && !cached) {
            ensureEffortOpts(typeId).then((opts) =>
              setModal((m) =>
                m && m.fieldLabel === LBL
                  ? { ...m, options: opts, optionsLoading: false }
                  : m,
              ),
            );
          }
          return;
        }
        case "tut": {
          const opts = options?.tutOptions ?? [];
          setModal({
            fieldLabel: "TUT (set)",
            fieldType: "hybrid",
            value: set.tut ?? "",
            options: opts,
            onSave: (v) =>
              saveSet({
                tut: v ? String(v) : null,
                tutMode: inferModeFromOptions(v ? String(v) : null, opts),
              }),
          });
          return;
        }
        case "tecnica": {
          const opts = options?.tecnicaOptions ?? [];
          setModal({
            fieldLabel: "Tecnica (set)",
            fieldType: "hybrid",
            value: set.tecnica ?? "",
            options: opts,
            onSave: (v) =>
              saveSet({
                tecnica: v ? String(v) : null,
                tecnicaMode: inferModeFromOptions(v ? String(v) : null, opts),
              }),
          });
          return;
        }
        case "rec": {
          // T2.B (#eda532b5) — Recupero edit modal. Pattern mirrors `tecnica`
          // (hybrid: catalog select + free-text fallback). Catalog comes from
          // ProgEditorOptions.recOptions (threaded from useEntityOptions).
          const opts = options?.recOptions ?? [];
          setModal({
            fieldLabel: "Recupero (set)",
            fieldType: "hybrid",
            value: set.rec ?? "",
            options: opts,
            onSave: (v) => saveSet({ rec: v === "" || v == null ? null : String(v) }),
          });
          return;
        }
        case "progressionRule":
          // PROG-DETAIL-NEW/4 — open the large-modal rule editor for this set.
          // Stage a copy of the current rule so the editor's incremental
          // `onChange(partial)` calls don't mutate the live programmazione
          // until the user clicks Save.
          setRuleEdit({
            weekIndex,
            setIndex,
            rule: { ...set.progressionRule },
          });
          return;
        case "miniSets":
          // PROG-DETAIL-NEW/5 — stage a deep-enough copy (rows are flat
          // objects, so map+spread suffices) so add/remove/edit operations
          // on the modal don't mutate the live programmazione until Save.
          setMiniSetsEdit({
            weekIndex,
            setIndex,
            miniSets: (set.miniSets ?? []).map((m) => ({ ...m })),
          });
          return;
        case "media":
          // PROG-DETAIL-NEW/6 — stage a shallow PrecompiledSet copy. We
          // cannot JSON-clone because `image` / `video` File handles would
          // be stripped; the shallow spread keeps them intact, and the
          // AllegatiPanel's onPatch only touches note/image/imageUrl/video/
          // videoUrl fields (no nested-object mutation).
          setMediaEdit({
            weekIndex,
            setIndex,
            stagedSet: { ...set },
          });
          return;
        default:
          return;
      }
    },
    [
      wireEdit,
      exerciseIndex,
      mutations,
      weeks,
      splitIndex,
      options,
      repSchemeCache,
      effortCache,
      ensureRepSchemeOpts,
      ensureEffortOpts,
      commit,
    ],
  );

  // PROG-DETAIL-NEW/4 — commit the staged rule for the focused set. The
  // page-level mutation deep-clones via `mutate()` so we just hand back the
  // staged object as the patch.
  const commitRule = useCallback(() => {
    if (wireEdit == null || exerciseIndex == null || !mutations || !ruleEdit)
      return;
    setRuleSaving(true);
    Promise.resolve(
      mutations.updateProgressionSet(
        splitIndex!,
        exerciseIndex,
        ruleEdit.weekIndex,
        ruleEdit.setIndex,
        { progressionRule: ruleEdit.rule },
      ),
    ).finally(() => {
      setRuleSaving(false);
      setRuleEdit(null);
    });
  }, [wireEdit, exerciseIndex, mutations, ruleEdit, splitIndex]);

  // "Aggiungi set" — append a new set carrying the configured rule.
  const commitAddSet = useCallback(() => {
    if (wireEdit == null || exerciseIndex == null || !mutations || !addSetEdit)
      return;
    setAddSetSaving(true);
    Promise.resolve(
      mutations.addProgressionSet(splitIndex!, exerciseIndex, addSetEdit.weekIndex, {
        label: null,
        trialLoad: null,
        progressionRule: addSetEdit.rule,
      }),
    ).finally(() => {
      setAddSetSaving(false);
      setAddSetEdit(null);
    });
  }, [wireEdit, exerciseIndex, mutations, addSetEdit, splitIndex]);

  // Whole-rule Copia/Incolla per set. Copy stages the set's progressionRule on
  // the shared clipboard slot; paste replaces the focused set's rule (running
  // normalizePastedRule so render-guard gating fields are filled) and commits
  // through the programmazione's own mutation channel — the catalog source is
  // never touched.
  const copyRuleFromSet = useCallback(
    (weekIndex: number, setIndex: number) => {
      const rule = weeks[weekIndex]?.sets?.[setIndex]?.progressionRule;
      if (!rule) return;
      copyPrecompiledRule(rule);
      showToast("ppd-rule-copied", "Regola copiata", undefined, "success");
    },
    [weeks, copyPrecompiledRule, showToast],
  );
  const pasteRuleToSet = useCallback(
    (weekIndex: number, setIndex: number) => {
      if (
        wireEdit == null ||
        exerciseIndex == null ||
        !mutations ||
        !copiedPrecompiledRule
      )
        return;
      const cloned = normalizePastedRule(
        JSON.parse(JSON.stringify(copiedPrecompiledRule)),
      );
      // Capture the prior rule so the paste can be undone.
      const prior = weeks[weekIndex]?.sets?.[setIndex]?.progressionRule;
      const priorRule = prior ? JSON.parse(JSON.stringify(prior)) : null;
      Promise.resolve(
        mutations.updateProgressionSet(
          splitIndex!,
          exerciseIndex,
          weekIndex,
          setIndex,
          { progressionRule: cloned },
        ),
      ).then(() =>
        showUndoToast("ppd-rule-pasted", "Regola incollata", () => {
          if (priorRule)
            mutations.updateProgressionSet(splitIndex!, exerciseIndex, weekIndex, setIndex, {
              progressionRule: priorRule,
            });
        }),
      );
    },
    [wireEdit, exerciseIndex, mutations, copiedPrecompiledRule, weeks, splitIndex, showUndoToast],
  );

  // Whole-SET Copia/Incolla per set — same programmazione-local mutation
  // channel as the rule paste; replaces the entire set (params + rule). The
  // catalog source progression is never touched.
  const copySetFromCard = useCallback(
    (weekIndex: number, setIndex: number) => {
      const s = weeks[weekIndex]?.sets?.[setIndex];
      if (!s) return;
      copyPrecompiledSet(s);
      showToast("ppd-set-copied", "Set copiato", undefined, "success");
    },
    [weeks, copyPrecompiledSet, showToast],
  );
  const pasteSetToCard = useCallback(
    (weekIndex: number, setIndex: number) => {
      if (
        wireEdit == null ||
        exerciseIndex == null ||
        !mutations ||
        !copiedPrecompiledSet
      )
        return;
      const cloned: PrecompiledSet = JSON.parse(
        JSON.stringify(copiedPrecompiledSet),
      );
      if (cloned.progressionRule) {
        cloned.progressionRule = normalizePastedRule(cloned.progressionRule);
      }
      const prior = weeks[weekIndex]?.sets?.[setIndex];
      const priorSet: PrecompiledSet | null = prior
        ? JSON.parse(JSON.stringify(prior))
        : null;
      Promise.resolve(
        mutations.updateProgressionSet(
          splitIndex!,
          exerciseIndex,
          weekIndex,
          setIndex,
          cloned,
        ),
      ).then(() =>
        showUndoToast("ppd-set-pasted", "Set incollato", () => {
          if (priorSet)
            mutations.updateProgressionSet(splitIndex!, exerciseIndex, weekIndex, setIndex, priorSet);
        }),
      );
    },
    [wireEdit, exerciseIndex, mutations, copiedPrecompiledSet, weeks, splitIndex, showUndoToast],
  );

  // Whole-WEEK Copia/Incolla — replaces the entire week (params + sets) via the
  // programmazione-local mutation channel. weekNumber is rewritten to the
  // target position; each pasted set's rule is normalized for render guards.
  const copyWeekFromCard = useCallback(
    (weekIndex: number) => {
      const w = weeks[weekIndex];
      if (!w) return;
      copyPrecompiledWeek(w);
      showToast("ppd-week-copied", "Settimana copiata", undefined, "success");
    },
    [weeks, copyPrecompiledWeek, showToast],
  );
  const pasteWeekToCard = useCallback(
    (weekIndex: number) => {
      if (
        wireEdit == null ||
        exerciseIndex == null ||
        !mutations ||
        !copiedPrecompiledWeek
      )
        return;
      const cloned: PrecompiledWeek = JSON.parse(
        JSON.stringify(copiedPrecompiledWeek),
      );
      cloned.weekNumber = weekIndex + 1;
      (cloned.sets ?? []).forEach((s) => {
        if (s.progressionRule) {
          s.progressionRule = normalizePastedRule(s.progressionRule);
        }
      });
      const prior = weeks[weekIndex];
      const priorWeek: PrecompiledWeek | null = prior
        ? JSON.parse(JSON.stringify(prior))
        : null;
      Promise.resolve(
        mutations.updateProgressionWeek(
          splitIndex!,
          exerciseIndex,
          weekIndex,
          cloned,
        ),
      ).then(() =>
        showUndoToast("ppd-week-pasted", "Settimana incollata", () => {
          if (priorWeek)
            mutations.updateProgressionWeek(splitIndex!, exerciseIndex, weekIndex, priorWeek);
        }),
      );
    },
    [wireEdit, exerciseIndex, mutations, copiedPrecompiledWeek, weeks, splitIndex, showUndoToast],
  );

  // PROG-DETAIL-NEW/5 — commit the staged miniSets list for the focused set.
  const commitMiniSets = useCallback(() => {
    if (
      wireEdit == null ||
      exerciseIndex == null ||
      !mutations ||
      !miniSetsEdit
    )
      return;
    setMiniSetsSaving(true);
    Promise.resolve(
      mutations.updateProgressionSet(
        splitIndex!,
        exerciseIndex,
        miniSetsEdit.weekIndex,
        miniSetsEdit.setIndex,
        { miniSets: miniSetsEdit.miniSets },
      ),
    ).finally(() => {
      setMiniSetsSaving(false);
      setMiniSetsEdit(null);
    });
  }, [wireEdit, exerciseIndex, mutations, miniSetsEdit, splitIndex]);

  // PROG-DETAIL-NEW/6 — commit the staged media patch (note + image / video +
  // their *Url string counterparts) for the focused set. The mutations hook
  // now strips File handles into FormData under the canonical bracket-path
  // keys, matching the existing form save flow.
  const commitMedia = useCallback(() => {
    if (
      wireEdit == null ||
      exerciseIndex == null ||
      !mutations ||
      !mediaEdit
    )
      return;
    const s = mediaEdit.stagedSet;
    // Per-role galleries (Media trainer / Media atleta) carry the multi-file
    // uploads. They MUST be included in the patch — omitting them dropped every
    // gallery upload on save (only the single primary image/video survived).
    const galleryHasNewFile = [
      ...(s.trainerMedia ?? []),
      ...(s.athleteMedia ?? []),
    ].some((m) => m.file instanceof File);
    const successMsg =
      s.video || s.videoUrl
        ? "Video salvato correttamente"
        : s.image || s.imageUrl
          ? "Immagine salvata correttamente"
          : galleryHasNewFile
            ? "Media salvati correttamente"
            : "Allegati del set salvati";
    const isUpload =
      s.image instanceof File || s.video instanceof File || galleryHasNewFile;
    // Fire the save synchronously (the patch — incl. File handles — is cloned
    // into the mutation before the first await), then close the modal at once
    // so the upload runs in the background, surfaced by the global bottom-right
    // indicator. The modal must NOT stay open/blocked during a slow upload.
    const saving = Promise.resolve(
      mutations.updateProgressionSet(
        splitIndex!,
        exerciseIndex,
        mediaEdit.weekIndex,
        mediaEdit.setIndex,
        {
          note: s.note ?? null,
          image: s.image,
          imageUrl: s.imageUrl ?? null,
          video: s.video,
          videoUrl: s.videoUrl ?? null,
          // BE restores the caller's non-owned role from the DB, so sending
          // both arrays is safe.
          trainerMedia: s.trainerMedia ?? null,
          athleteMedia: s.athleteMedia ?? null,
        },
        successMsg,
      ),
    );
    if (isUpload) track(saving);
    setMediaEdit(null);
  }, [wireEdit, exerciseIndex, mutations, mediaEdit, splitIndex, track]);

  // PROG-DETAIL-NEW/4 — Q2: large Modal on >=md, fullscreen on narrower
  // viewports. We can't conditionally swap width at every render of the
  // editor (would tear the Modal), so we read the breakpoint here and the
  // Modal picks width / style accordingly. Grid.useBreakpoint is cheap and
  // matches the pattern already used in ExerciseCardView.
  const screens = Grid.useBreakpoint();
  const isWideViewport = !!screens.md;

  if (weeks.length === 0) {
    return <div className="ppd-no-weeks">Nessuna settimana configurata</div>;
  }

  const editingEnabled = wireEdit != null && (isTrainer || isAthlete);

  // PROG-DETAIL-NEW/4 — resolve the prev-* context the ProgressionRuleEditor
  // needs (mirroring the live editor's mount in ExerciseProgressionSetRow).
  // Done lazily here so we don't recompute when the modal is closed.
  const ruleEditorCtx = (() => {
    if (!ruleEdit) return null;
    const { weekIndex, setIndex } = ruleEdit;
    const week = weeks[weekIndex];
    if (!week) return null;
    const sets = week.sets ?? [];
    const prevWeek = weekIndex > 0 ? weeks[weekIndex - 1] : null;
    const prevWeekSets = prevWeek?.sets ?? [];
    const prevWeekSet = prevWeekSets[setIndex] ?? null;
    const prevSetInWeek = setIndex > 0 ? sets[setIndex - 1] ?? null : null;
    const prevWeekHypLoad =
      weekIndex > 0 ? hypLoadGrid[weekIndex - 1]?.[setIndex] ?? null : null;
    const prevSetHypLoad =
      setIndex > 0 ? hypLoadGrid[weekIndex]?.[setIndex - 1] ?? null : null;
    return {
      week,
      prevWeekSet,
      prevSetInWeek,
      prevWeekHypLoad,
      prevSetHypLoad,
      prevWeekSetRule: prevWeekSet?.progressionRule ?? null,
      prevSetRule: prevSetInWeek?.progressionRule ?? null,
    };
  })();

  // Prev-slot context for the NEW set (lands at index = week.sets.length), so
  // the rule editor's projections + the ancestor source-picker resolve.
  const addSetEditorCtx = (() => {
    if (!addSetEdit) return null;
    const { weekIndex } = addSetEdit;
    const week = weeks[weekIndex];
    if (!week) return null;
    const sets = week.sets ?? [];
    const setIndex = sets.length;
    const prevWeek = weekIndex > 0 ? weeks[weekIndex - 1] : null;
    const prevWeekSet = prevWeek?.sets?.[setIndex] ?? null;
    const prevSetInWeek = setIndex > 0 ? sets[setIndex - 1] ?? null : null;
    const prevWeekHypLoad =
      weekIndex > 0 ? hypLoadGrid[weekIndex - 1]?.[setIndex] ?? null : null;
    const prevSetHypLoad =
      setIndex > 0 ? hypLoadGrid[weekIndex]?.[setIndex - 1] ?? null : null;
    return {
      week,
      setIndex,
      prevWeekSet,
      prevSetInWeek,
      prevWeekHypLoad,
      prevSetHypLoad,
      prevWeekSetRule: prevWeekSet?.progressionRule ?? null,
      prevSetRule: prevSetInWeek?.progressionRule ?? null,
    };
  })();

  return (
    <>
      {weeks.map((week, i) => {
        const flags = tecnicaFlagsFor(week);
        return (
        <WeekDetailCard
          key={i}
          week={week}
          index={i}
          includeXReps={flags.includeXReps}
          dropXReps={flags.dropXReps}
          hypLoads={hypLoadGrid[i] ?? []}
          hypReps={hypRepsGrid[i] ?? []}
          prevWeekSets={i > 0 ? weeks[i - 1].sets ?? null : null}
          prevWeekHypLoads={i > 0 ? hypLoadGrid[i - 1] ?? null : null}
          weeks={weeks}
          options={options}
          onEditWeekField={
            editingEnabled ? (field) => openWeekEdit(i, field) : null
          }
          onEditSetField={
            editingEnabled
              ? (field, setIndex) => openSetEdit(i, setIndex, field)
              : null
          }
          onCopySetRule={
            editingEnabled ? (setIndex) => copyRuleFromSet(i, setIndex) : null
          }
          onPasteSetRule={
            editingEnabled ? (setIndex) => pasteRuleToSet(i, setIndex) : null
          }
          canPasteRule={!!copiedPrecompiledRule}
          onCopySet={
            editingEnabled ? (setIndex) => copySetFromCard(i, setIndex) : null
          }
          onPasteSet={
            editingEnabled ? (setIndex) => pasteSetToCard(i, setIndex) : null
          }
          canPasteSet={!!copiedPrecompiledSet}
          onCopyWeek={editingEnabled ? () => copyWeekFromCard(i) : null}
          onPasteWeek={editingEnabled ? () => pasteWeekToCard(i) : null}
          canPasteWeek={!!copiedPrecompiledWeek}
          onSetWeekDate={
            editingEnabled && splitIndex != null && exerciseIndex != null
              ? (iso) =>
                  mutations!.updateProgressionWeek(splitIndex!, exerciseIndex!, i, {
                    date: iso,
                  })
              : null
          }
          onAddSet={
            editingEnabled
              ? () => setAddSetEdit({ weekIndex: i, rule: emptyRule() })
              : null
          }
          canEditField={canEditField}
          displayMode={displayMode}
          isTrainer={isTrainer}
        />
        );
      })}
      {modal && (
        <InlineEditModal
          open
          fieldLabel={modal.fieldLabel}
          fieldType={modal.fieldType}
          value={modal.value}
          options={modal.options}
          optionsLoading={modal.optionsLoading}
          saving={saving}
          onSave={(v) => modal.onSave(v)}
          onClose={closeModal}
        />
      )}
      {ruleEdit && ruleEditorCtx && (
        <Modal
          open
          title={`Modifica regola di progressione — Sett. ${ruleEdit.weekIndex + 1} · Set ${ruleEdit.setIndex + 1}`}
          okText="Salva"
          cancelText="Annulla"
          confirmLoading={ruleSaving}
          onOk={commitRule}
          onCancel={() => setRuleEdit(null)}
          // PROG-DETAIL-NEW/4 / Q2 — large width on >=md, fullscreen on narrow.
          width={isWideViewport ? 900 : "100%"}
          style={isWideViewport ? { top: 24 } : { top: 0, paddingBottom: 0, maxWidth: "100%" }}
          styles={isWideViewport ? undefined : { body: { maxHeight: "75vh", overflowY: "auto" } }}
          centered={isWideViewport}
          destroyOnClose
        >
          <ProgressionRuleEditor
            rule={ruleEdit.rule}
            isFirstWeek={ruleEdit.weekIndex === 0}
            setIndex={ruleEdit.setIndex}
            onChange={(partial) =>
              setRuleEdit((prev) =>
                prev ? { ...prev, rule: { ...prev.rule, ...partial } } : prev,
              )
            }
            weekRepScheme={ruleEditorCtx.week.repScheme}
            prevWeekHypLoad={ruleEditorCtx.prevWeekHypLoad}
            prevWeekSetRule={ruleEditorCtx.prevWeekSetRule}
            prevSetHypLoad={ruleEditorCtx.prevSetHypLoad}
            prevSetRule={ruleEditorCtx.prevSetRule}
            prevWeekSet={ruleEditorCtx.prevWeekSet}
            prevSetInWeek={ruleEditorCtx.prevSetInWeek}
            /* Thread weeks + coordinates so the multi-source "Sorgente carico
               (ancestor)" picker renders here just like in the library
               form / detail (enumerateSourceCandidates needs the full grid). */
            weeks={weeks}
            wi={ruleEdit.weekIndex}
            si={ruleEdit.setIndex}
            gridHypLoad={hypLoadGrid[ruleEdit.weekIndex]?.[ruleEdit.setIndex] ?? null}
            gridHypReps={hypRepsGrid[ruleEdit.weekIndex]?.[ruleEdit.setIndex] ?? null}
            copiedBoxConfig={copiedPrecompiledRuleBox}
            onCopyBoxConfig={handleCopyBoxConfig}
          />
        </Modal>
      )}
      {addSetEdit && addSetEditorCtx && (
        <Modal
          open
          title={`Aggiungi set — Sett. ${addSetEdit.weekIndex + 1} · Set ${addSetEditorCtx.setIndex + 1}`}
          okText="Aggiungi"
          cancelText="Annulla"
          confirmLoading={addSetSaving}
          onOk={commitAddSet}
          onCancel={() => setAddSetEdit(null)}
          width={isWideViewport ? 900 : "100%"}
          style={isWideViewport ? { top: 24 } : { top: 0, paddingBottom: 0, maxWidth: "100%" }}
          styles={isWideViewport ? undefined : { body: { maxHeight: "75vh", overflowY: "auto" } }}
          centered={isWideViewport}
          destroyOnClose
        >
          <ProgressionRuleEditor
            rule={addSetEdit.rule}
            isFirstWeek={addSetEdit.weekIndex === 0}
            setIndex={addSetEditorCtx.setIndex}
            onChange={(partial) =>
              setAddSetEdit((prev) =>
                prev ? { ...prev, rule: { ...prev.rule, ...partial } } : prev,
              )
            }
            weekRepScheme={addSetEditorCtx.week.repScheme}
            prevWeekHypLoad={addSetEditorCtx.prevWeekHypLoad}
            prevWeekSetRule={addSetEditorCtx.prevWeekSetRule}
            prevSetHypLoad={addSetEditorCtx.prevSetHypLoad}
            prevSetRule={addSetEditorCtx.prevSetRule}
            prevWeekSet={addSetEditorCtx.prevWeekSet}
            prevSetInWeek={addSetEditorCtx.prevSetInWeek}
            weeks={weeks}
            wi={addSetEdit.weekIndex}
            si={addSetEditorCtx.setIndex}
            copiedBoxConfig={copiedPrecompiledRuleBox}
            onCopyBoxConfig={handleCopyBoxConfig}
          />
        </Modal>
      )}
      {miniSetsEdit && (() => {
        const { weekIndex, setIndex, miniSets } = miniSetsEdit;
        const focusedSet =
          weeks[weekIndex]?.sets?.[setIndex];
        const baseLoad =
          (focusedSet?.workLoad != null ? Number(focusedSet.workLoad) : 0) ||
          (focusedSet?.trialLoad != null ? Number(focusedSet.trialLoad) : 0);
        const projections = computeMiniSetLoads(baseLoad, miniSets);
        const updateRow = (i: number, patch: Partial<MiniSet>) =>
          setMiniSetsEdit((prev) =>
            prev
              ? {
                  ...prev,
                  miniSets: prev.miniSets.map((m, idx) =>
                    idx === i ? { ...m, ...patch } : m,
                  ),
                }
              : prev,
          );
        const addRow = () =>
          setMiniSetsEdit((prev) =>
            prev
              ? {
                  ...prev,
                  miniSets: [
                    ...prev.miniSets,
                    { reps: "", deltaType: "%", deltaValue: 0 },
                  ],
                }
              : prev,
          );
        const removeRow = (i: number) =>
          setMiniSetsEdit((prev) =>
            prev
              ? {
                  ...prev,
                  miniSets: prev.miniSets.filter((_, idx) => idx !== i),
                }
              : prev,
          );
        return (
          <Modal
            open
            title={`Mini-set (drop set) — Sett. ${weekIndex + 1} · Set ${setIndex + 1}`}
            okText="Salva"
            cancelText="Annulla"
            confirmLoading={miniSetsSaving}
            onOk={commitMiniSets}
            onCancel={() => setMiniSetsEdit(null)}
            width={isWideViewport ? 720 : "100%"}
            style={
              isWideViewport
                ? { top: 24 }
                : { top: 0, paddingBottom: 0, maxWidth: "100%" }
            }
            styles={
              isWideViewport
                ? undefined
                : { body: { maxHeight: "75vh", overflowY: "auto" } }
            }
            centered={isWideViewport}
            destroyOnClose
            data-testid={`prog-detail-minisets-modal-${weekIndex}-${setIndex}`}
          >
            {miniSets.length === 0 && (
              <Typography.Paragraph type="secondary">
                {isTrainer
                  ? "Nessun mini-set. Aggiungine uno con il pulsante in fondo."
                  : "Nessun mini-set impostato dal trainer."}
              </Typography.Paragraph>
            )}
            {miniSets.map((m, i) => {
              const proj = projections[i];
              return (
                <div
                  key={i}
                  style={{
                    marginBottom: 10,
                    padding: 8,
                    border: "1px solid var(--app-gray-200)",
                    borderRadius: 8,
                  }}
                >
                  <Row gutter={[8, 8]} align="middle">
                    <Col xs={8} sm={6}>
                      <Input
                        value={String(m.reps ?? "")}
                        placeholder="reps (es. 8 o 6-8)"
                        disabled={!isTrainer}
                        onChange={(e) => updateRow(i, { reps: e.target.value })}
                        data-testid={`prog-detail-miniset-reps-${weekIndex}-${setIndex}-${i}`}
                      />
                    </Col>
                    <Col xs={6} sm={4}>
                      <Select
                        style={{ width: "100%" }}
                        value={m.deltaType}
                        disabled={!isTrainer}
                        onChange={(val) =>
                          updateRow(i, { deltaType: val as "%" | "kg" })
                        }
                        options={[
                          { label: "%", value: "%" },
                          { label: "kg", value: "kg" },
                        ]}
                        data-testid={`prog-detail-miniset-delta-type-${weekIndex}-${setIndex}-${i}`}
                      />
                    </Col>
                    <Col xs={6} sm={4}>
                      <DecimalInputNumber
                        style={{ width: "100%" }}
                        min={0}
                        value={m.deltaValue}
                        disabled={!isTrainer}
                        onChange={(v) =>
                          updateRow(i, {
                            deltaValue: typeof v === "number" ? v : 0,
                          })
                        }
                        data-testid={`prog-detail-miniset-delta-value-${weekIndex}-${setIndex}-${i}`}
                      />
                    </Col>
                    <Col xs={4} sm={10}>
                      <Space size={6}>
                        {proj && proj.load > 0 && (
                          <Tag
                            color="cyan"
                            data-testid={`prog-detail-miniset-proj-${weekIndex}-${setIndex}-${i}`}
                          >
                            {proj.load} kg
                          </Tag>
                        )}
                        {isTrainer && (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => removeRow(i)}
                            aria-label={`Rimuovi mini-set ${i + 1}`}
                            data-testid={`prog-detail-miniset-remove-${weekIndex}-${setIndex}-${i}`}
                          />
                        )}
                      </Space>
                    </Col>
                  </Row>
                  {/* Esecuzione — work reps/load del mini-set (editabile anche
                      dall'atleta). */}
                  <Row gutter={[8, 8]} align="middle" style={{ marginTop: 6 }}>
                    <Col xs={12} sm={6}>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        Work reps
                      </Typography.Text>
                      <Input
                        value={m.workReps != null ? String(m.workReps) : ""}
                        placeholder="reps eseguite"
                        onChange={(e) => {
                          const v = e.target.value.trim();
                          updateRow(i, { workReps: v === "" ? null : v });
                        }}
                        data-testid={`prog-detail-miniset-workreps-${weekIndex}-${setIndex}-${i}`}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        Work load
                      </Typography.Text>
                      <DecimalInputNumber
                        style={{ width: "100%" }}
                        value={m.workLoad ?? null}
                        placeholder="kg"
                        onChange={(v) =>
                          updateRow(i, {
                            workLoad: v == null ? null : Number(v),
                          })
                        }
                        data-testid={`prog-detail-miniset-workload-${weekIndex}-${setIndex}-${i}`}
                      />
                    </Col>
                  </Row>
                </div>
              );
            })}
            {isTrainer && (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={addRow}
                data-testid={`prog-detail-miniset-add-${weekIndex}-${setIndex}`}
                style={{ marginTop: 4 }}
              >
                Aggiungi mini-set
              </Button>
            )}
          </Modal>
        );
      })()}
      {mediaEdit && (
        <Modal
          open
          title={`Allegati — Sett. ${mediaEdit.weekIndex + 1} · Set ${mediaEdit.setIndex + 1}`}
          okText="Salva"
          cancelText="Annulla"
          onOk={commitMedia}
          onCancel={() => setMediaEdit(null)}
          // PROG-DETAIL-NEW/6 — match the DETAIL-NEW/4 sizing pattern. Media
          // panel is compact, so a 560 desktop width is enough; narrow stays
          // fullscreen for the upload affordance.
          width={isWideViewport ? 560 : "100%"}
          style={
            isWideViewport
              ? { top: 24 }
              : { top: 0, paddingBottom: 0, maxWidth: "100%" }
          }
          styles={
            isWideViewport
              ? undefined
              : { body: { maxHeight: "75vh", overflowY: "auto" } }
          }
          centered={isWideViewport}
          destroyOnClose
          data-testid={`prog-detail-media-modal-${mediaEdit.weekIndex}-${mediaEdit.setIndex}`}
        >
          <AllegatiPanel
            wi={mediaEdit.weekIndex}
            si={mediaEdit.setIndex}
            set={mediaEdit.stagedSet}
            mediaLocked={false}
            chromeless
            showPrimaryMedia={false}
            canUploadTrainer={isTrainer}
            canUploadAthlete={isAthlete}
            onPatch={(partial) =>
              setMediaEdit((prev) =>
                prev
                  ? { ...prev, stagedSet: { ...prev.stagedSet, ...partial } }
                  : prev,
              )
            }
          />
        </Modal>
      )}
    </>
  );
}
